import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { financialAPI, unitAPI, advancedAmountAPI, userAPI, bankBalanceAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import '../../styles/ThemeVariables.css';
import '../../styles/Dashboard.css';
import axios from 'axios';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

// Helper for month name
const monthNames = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Financials = () => {
  const navigate = useNavigate();
  // const { currentUser } = useContext(AuthContext);
  const { settings } = useSettings();
  const [transactions, setTransactions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    loading: true,
    plan: null,
    isFreePlan: false,
    error: null
  });
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  // Filter states

  const [filters, setFilters] = useState({
    category: 'all',
    unit_id: 'all',
    search: '',
    status: 'all',
    month_id: 'all',
    year: 'all'
  });

  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Get dwelling unit name
  const unitSingular = formatDwellingUnit(settings);
  // const unitPlural = formatDwellingUnit(settings, null, true);

  // For transaction form
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'payment',
    amount: '',
    category: '',
    unit_id: '',
    date: new Date().toISOString().split('T')[0],
    payment_date: new Date().toISOString().split('T')[0],
    partially_payment_date: '',
    partial_paid_amount: '',
    already_partial_paid_amount: '',
    remarks: '',
    status: 'pending',
    remaining_amount: '',
    year: new Date().getFullYear().toString(),
    payment_method: 'cash_payment',
    document_url: [] // Changed to array for multiple document uploads
  });

  // advance amount integration for transactions
  const [unitAdvancedBalance, setUnitAdvancedBalance] = useState(0);
  const [useAdvancedAmount, setUseAdvancedAmount] = useState(false);
  const [advancedAmountToUse, setAdvancedAmountToUse] = useState(null);
  const [advancedAmountError, setAdvancedAmountError] = useState(null); // Error for booking category validation
  const [paymentBreakdown, setPaymentBreakdown] = useState({
    total: 0,
    advancedUsed: 0,
    cashPayment: 0
  });

  // Store original/default values for partially_paid transactions
  const [originalPartiallyPaidValues, setOriginalPartiallyPaidValues] = useState({
    partial_paid_amount: '',
    remaining_amount: '',
    useAdvancedAmount: false,
    advancedAmountToUse: null
  });

  // Track if partially_payment_date is from database (for edit mode)
  const [isPartiallyPaymentDateFromDB, setIsPartiallyPaymentDateFromDB] = useState(false);

  // advance amount form state
  const [showAdvancedForm, setShowAdvancedForm] = useState(false);
  const [advancedFormData, setAdvancedFormData] = useState({
    unit_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    remarks: '',
    advance_type: 'credit', // credit or debit
    document_url: []
  });

  // Stats summary
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    balance: 0,
    pendingPayments: 0
  });

  // Advance payments total
  const [advancedPaymentsTotal, setAdvancedPaymentsTotal] = useState(0);

  // Owner payments state
  const [ownerPayments, setOwnerPayments] = useState([]);
  const [ownerPaymentsLoading, setOwnerPaymentsLoading] = useState(false);
  const [ownerPaymentsError, setOwnerPaymentsError] = useState(null);

  // advance amounts state
  const [advancedAmounts, setAdvancedAmounts] = useState([]);
  const [advancedAmountsLoading, setAdvancedAmountsLoading] = useState(false);
  const [advancedAmountsError, setAdvancedAmountsError] = useState(null);

  // Bank balance state
  const [showBankBalanceModal, setShowBankBalanceModal] = useState(false);
  const [bankBalanceFormData, setBankBalanceFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [bankBalanceLoading, setBankBalanceLoading] = useState(false);
  const [bankBalanceError, setBankBalanceError] = useState(null);
  const [bankBalanceSuccess, setBankBalanceSuccess] = useState(null);
  const [isEditBankBalance, setIsEditBankBalance] = useState(false);
  const [currentBankBalanceId, setCurrentBankBalanceId] = useState(null);
  const [latestBankBalance, setLatestBankBalance] = useState(null);
  const [isBalanceLoaded, setIsBalanceLoaded] = useState(false);

  // Ref to store timeout for cleanup
  const timeoutRef = useRef(null);

  // File upload state for expense documents
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // File upload state for advance amount documents
  const [selectedAdvancedFiles, setSelectedAdvancedFiles] = useState([]);
  
  // Edit mode state for advance amounts
  const [isEditAdvancedMode, setIsEditAdvancedMode] = useState(false);
  const [currentAdvancedId, setCurrentAdvancedId] = useState(null);

  // Filter transactions based on filters and activeTab

  useEffect(() => {

    let filtered = [...transactions];
    // Apply tab filter first

    if (activeTab === 'income') {
      filtered = filtered.filter(t => t.type === 'payment');
    } else if (activeTab === 'expenses') {
      filtered = filtered.filter(t => t.type === 'expense');
    }

    // Apply category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(t => t.category === filters.category);
    }

    // Apply unit filter
    if (filters.unit_id !== 'all') {
      filtered = filtered.filter(t => t.unit_id === parseInt(filters.unit_id));
    }

    // Apply search filter
    if (filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(t =>
        (t.category && t.category.toLowerCase().includes(searchTerm)) ||
        (t.remarks && t.remarks.toLowerCase().includes(searchTerm)) ||
        (t.villa_number && t.villa_number.toLowerCase().includes(searchTerm)) ||
        (t.amount && t.amount.toString().includes(searchTerm))
      );
    }

    // Apply status filter
    if (filters.status !== 'all') {
      if (filters.status === 'pending') {
        // When filtering for pending, include both pending and partially_paid transactions
        filtered = filtered.filter(t => t.status === 'pending' || t.status === 'partially_paid');
      } else {
        filtered = filtered.filter(t => t.status === filters.status);
      }
    }

    // Apply month filter
    if (filters.month_id !== 'all') {
      filtered = filtered.filter(t => t.month_id === parseInt(filters.month_id));
    }

    // Apply year filter
    if (filters.year !== 'all') {
      filtered = filtered.filter(t => t.year === parseInt(filters.year));
    }
    setFilteredTransactions(filtered);
  }, [transactions, activeTab, filters]);

  // Pagination state for owner payments
  const [ownerPaymentsPage, setOwnerPaymentsPage] = useState(1);
  const ownerPaymentsPerPage = 20;
  const ownerPaymentsIndexOfLast = ownerPaymentsPage * ownerPaymentsPerPage;
  const ownerPaymentsIndexOfFirst = ownerPaymentsIndexOfLast - ownerPaymentsPerPage;
  const paginatedOwnerPayments = ownerPayments.slice(ownerPaymentsIndexOfFirst, ownerPaymentsIndexOfLast);
  const ownerPaymentsTotalPages = Math.ceil(ownerPayments.length / ownerPaymentsPerPage);

  // Delete modal state for owner payments
  const [showDeleteOwnerPayment, setShowDeleteOwnerPayment] = useState(false);
  const [ownerPaymentToDelete, setOwnerPaymentToDelete] = useState(null);

  // Pagination state for transactions (like owner payments)
  const [transactionsPage, setTransactionsPage] = useState(1);
  const transactionsPerPage = 20;
  const transactionsIndexOfLast = transactionsPage * transactionsPerPage;
  const transactionsIndexOfFirst = transactionsIndexOfLast - transactionsPerPage;
  const paginatedTransactions = filteredTransactions.slice(transactionsIndexOfFirst, transactionsIndexOfLast);
  const transactionsTotalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  // State for viewing transaction details

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTransaction, setViewTransaction] = useState(null);

  // State for document upload modal
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [documentUploadTransaction, setDocumentUploadTransaction] = useState(null);
  const [documentUploadFiles, setDocumentUploadFiles] = useState([]);
  const [documentUploadError, setDocumentUploadError] = useState(null);
  const [documentUploadSuccess, setDocumentUploadSuccess] = useState(null);

  useEffect(() => {
    setTransactionsPage(1);
  }, [activeTab]);

  useEffect(() => {
    setTransactionsPage(1);
  }, [filters]);

  // Generate a unique CSS class name based on the current settings
  const themeClassName = `theme-header-${Date.now()}`;

  // Get theme colors from settings
  const bgColor = settings.block_title_bg_color || '#f8f9fa';
  const textColor = settings.block_title_color || '#8B4513';

  // Add CSS styles directly to the document head
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      /* Apply direct styles to these elements */
      .card-header.${themeClassName},
      .modal-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
        border-color: ${bgColor} !important;
      }
      
      /* Force all children to have the text color */
      .card-header.${themeClassName} *,
      .modal-header.${themeClassName} * {
        color: ${textColor} !important;
      }
      
      /* Override any other rules */
      .card .card-header.${themeClassName},
      .modal .modal-header.${themeClassName},
      .card-header.${themeClassName},
      .modal-header.${themeClassName},
      div.card-header.${themeClassName},
      div.modal-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
      
      /* Direct style application */
      #root .card-header.${themeClassName},
      html body .card-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
      
      /* For Description section specifically */
      .card-header.${themeClassName}[class*="theme-header"] {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      if (styleTag.parentNode) {
        document.head.removeChild(styleTag);
      }
    };
  }, [themeClassName, bgColor, textColor]);

  // Check subscription plan
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        // Include tenant/organization ID to target the correct subscription
        const userStr = sessionStorage.getItem('user');
        const userObj = userStr ? JSON.parse(userStr) : {};
        const tenantId = userObj.tenant_id || userObj.organization_id;
        const response = await axios.get('/api/saas/membership/details', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { tenant_id: tenantId }
        });

        if (response.data && response.data.success) {
          const planName = response.data.data.plan?.name || 'Unknown';
          setSubscriptionStatus({
            loading: false,
            plan: planName,
            isFreePlan: planName === 'Free',
            error: null
          });
        } else {
          // Default to Free plan if no subscription found
          setSubscriptionStatus({
            loading: false,
            plan: 'Free',
            isFreePlan: true,
            error: null
          });
        }
      } catch (error) {
        // Default to Free plan if there's an error
        setSubscriptionStatus({
          loading: false,
          plan: 'Free',
          isFreePlan: true,
          error: 'Failed to check subscription status'
        });
      }
    };

    checkSubscription();
  }, []);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Track advancedPaymentsTotal state changes
  useEffect(() => {
    console.log('💰 advancedPaymentsTotal state changed to:', advancedPaymentsTotal);
  }, [advancedPaymentsTotal]);

  // Currency formatter function for use in component
  const formatCurrency = (amount) => {
    // Always use INR as currency and ₹ as symbol
    const currencyCode = 'INR';
    const currencySymbol = '₹';

    try {
      // Try using Intl.NumberFormat if available
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Fallback to basic formatting
      return `${currencySymbol} ${parseFloat(amount).toFixed(2)}`;
    }
  };

  // Safe wrapper for formatCurrency to handle undefined or null values
  const safeFormatCurrency = (amount) => {
    // If amount is undefined or null, return zero formatted as currency
    return formatCurrency(amount !== undefined && amount !== null ? amount : 0);
  };

  // Filter handlers
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      category: 'all',
      unit_id: 'all',
      search: '',
      status: 'all',
      month_id: 'all',
      year: 'all'
    });
  };

  // Get unique categories from transactions
  const getUniqueCategories = () => {
    const categories = [...new Set(transactions.map(t => t.category).filter(Boolean))];
    return categories.sort();
  };

  // Get unique statuses from transactions
  const getUniqueStatuses = () => {
    const statuses = ['paid', 'pending', 'hold', 'partially_paid'];
    return statuses.sort();
  };

  // Get all months (1-12) statically
  const getUniqueMonths = () => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  };

  // Get unique years from transactions
  const getUniqueYears = () => {
    const years = [...new Set(transactions.map(t => t.year).filter(Boolean))];
    return years.sort((a, b) => b - a); // Sort years in descending order (newest first)
  };

  // Fetch complete financial summary from API
  const fetchCompleteSummary = useCallback(async () => {
    try {
      console.log('🔄 Fetching complete financial summary from API...');
      const response = await financialAPI.getCompleteSummary();

      if (response.data && response.data.success) {
        const summary = response.data.data;
        console.log('✅ Complete summary received:', summary);

        // Update all states with API response
        setStats({
          totalIncome: summary.totalIncome || 0,
          totalExpenses: summary.totalExpenses || 0,
          balance: summary.balance || 0,
          pendingPayments: summary.pendingPayments || 0
        });
        setAdvancedPaymentsTotal(summary.advancedPaymentsTotal || 0);

        return summary;
      } else {
        throw new Error('Invalid API response structure');
      }
    } catch (error) {
      console.error('❌ Error fetching complete summary:', error);

      // Set error but don't throw to prevent breaking the component
      setError('Failed to load financial summary. Please try again.');

      // Return default values to prevent crashes
      return {
        totalIncome: 0,
        totalExpenses: 0,
        balance: 0,
        pendingPayments: 0,
        advancedPaymentsTotal: 0
      };
    }
  }, []);

  // Helper function to refresh all financial stats from API
  const refreshAllStats = useCallback(async () => {
    console.log('🔄 refreshAllStats called - fetching from API...');
    try {
      await fetchCompleteSummary();
      console.log('✅ Stats refreshed successfully from API');
    } catch (error) {
      console.error('❌ Error refreshing stats:', error);
    }
  }, [fetchCompleteSummary]);

  // Handle clicking on pending payments card to filter

  const handlePendingPaymentsClick = () => {
    // Set filters to show pending transactions (this will include both pending and partially_paid in the display)
    setFilters({ category: 'all', unit_id: 'all', search: '', status: 'pending', month_id: 'all', year: 'all' });
    setActiveTab('all'); // Make sure we're showing all transactions tab
    window.scrollBy({
      top: window.innerHeight * 0.5,
      behavior: 'smooth'
    });
  };



  // Fetch transactions and villas
  const fetchFinancialData = useCallback(async () => {
    // Skip fetching if user is on a free plan
    if (subscriptionStatus.isFreePlan && !subscriptionStatus.loading) {
      setLoading(false);
      return;
    }

    // Only fetch if subscription check is complete and not free plan
    if (!subscriptionStatus.loading && !subscriptionStatus.isFreePlan) {
      setLoading(true);
      try {
        // Get expenses separately to ensure we have them
        const expensesResponse = await financialAPI.getAllExpenses();

        // Get real data from API including owners
        const [unitsResponse, financialsResponse, paymentStatusResponse, ownersResponse] = await Promise.all([
          unitAPI.getAllUnits(),
          financialAPI.getAllFinancials(),
          financialAPI.getVillaPaymentStatus(),
          userAPI.getAllUsers({ role: 'owner' })
        ]);

        // Combine regular transactions with expenses, removing any duplicates
        let allTransactionsData = [...financialsResponse.data];

        // Add any expenses that aren't already in the main transactions list
        const expenseIds = new Set(allTransactionsData.filter(t => t.type === 'expense').map(t => t.id));
        for (const expense of expensesResponse.data) {
          if (!expenseIds.has(expense.id)) {
            allTransactionsData.push(expense);
          }
        }

        allTransactionsData.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at || 0);
          const dateB = new Date(b.updated_at || b.created_at || 0);
          return dateB - dateA;
        });

        const unitsData = unitsResponse.data;
        const ownersData = ownersResponse.data;
        const transactionsData = allTransactionsData; // Use the combined data with expenses

        // Log available unit types for debugging
        const unitTypes = new Set();
        unitsData.forEach(unit => {
          if (unit.unit_type) {
            unitTypes.add(unit.unit_type);
          }
        });

        if (unitsData.length === 0) {
          console.warn('No units returned from API!');
        }

        // Enrich units data with owner information
        const enrichedUnits = unitsData.map(unit => {
          const owner = ownersData.find(o => o.id === unit.owner_id);
          return {
            ...unit,
            owner_name: owner ? owner.name : unit.owner_name,
            contact_email: owner ? owner.email : unit.contact_email,
            contact_phone: owner && owner.phone ? owner.phone : unit.contact_phone
          };
        });

        // Create a unit lookup object for faster unit data retrieval
        const unitLookup = {};
        enrichedUnits.forEach(unit => {
          unitLookup[unit.id] = unit;
        });

        // Enhance transactions with unit info
        const enhancedTransactions = transactionsData.map(transaction => {
          if (transaction.unit_id && unitLookup[transaction.unit_id]) {
            const unit = unitLookup[transaction.unit_id];
            return {
              ...transaction,
              villa_number: unit.villa_number,
              unit_type: unit.unit_type
            };
          }
          return transaction;
        });

        setUnits(enrichedUnits);
        setTransactions(enhancedTransactions);

        // Get financial summary from the API or calculate it
        try {
          const summaryResponse = await financialAPI.getFinancialSummary();

          // Check if the summary values are all zeros despite having transactions
          if (summaryResponse.data &&
            summaryResponse.data.totalIncome === 0 &&
            summaryResponse.data.totalExpenses === 0 &&
            transactionsData.length > 0) {
            console.log('API returned zeros despite having transactions. Using fallback calculation.');
            throw new Error('Summary data inconsistent with transactions');
          }

          // Use our new complete summary API instead
          await fetchCompleteSummary();
        } catch (summaryErr) {
          console.error('Error fetching financial summary or data inconsistent:', summaryErr);

          // Use our new complete summary API instead
          await fetchCompleteSummary();
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching financial data:', err);
        setError('Failed to load financial data. Please try again.');

        // Clear any existing data on error instead of using mock data
        setUnits([]);
        setTransactions([]);
        setStats({
          totalIncome: 0,
          totalExpenses: 0,
          balance: 0,
          pendingPayments: 0
        });
      } finally {
        setLoading(false);
      }
    }
  }, [subscriptionStatus]);

  // Initial data fetch
  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData]);

  // Initial stats fetch
  useEffect(() => {
    if (!subscriptionStatus.loading && !subscriptionStatus.isFreePlan) {
      console.log('📊 Initial stats fetch triggered');
      fetchCompleteSummary();
    }
  }, [subscriptionStatus, fetchCompleteSummary]);

  const getMonthName = (monthId) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[parseInt(monthId, 10) - 1] || '';
  };

  // Fetch advance amounts function (extracted for reusability)
  const fetchAdvancedAmounts = async () => {
    console.log('🔄 fetchAdvancedAmounts called');
    setAdvancedAmountsLoading(true);
    setAdvancedAmountsError(null);
    try {
      console.log('🔄 Calling advancedAmountAPI.getAll()...');
      const response = await advancedAmountAPI.getAll();
      console.log('🔄 API response received:', response);
      // The controller returns {success: true, data: advancedAmounts}
      // So we need to access response.data.data to get the actual array
      const apiData = response.data?.data || response.data;
      const data = Array.isArray(apiData) ? apiData : [];

      console.log('📦 Received advance amounts data:', {
        responseStructure: Object.keys(response.data || {}),
        apiDataLength: apiData?.length || 0,
        dataLength: data.length,
        sampleData: data.slice(0, 2),
        allData: data
      });

      setAdvancedAmounts(data);

      console.log('🔄 Advance amounts loaded, refreshing stats from API...');

      // Refresh stats from API after loading advance amounts
      await refreshAllStats();
    } catch (err) {
      console.error('❌ Error in fetchAdvancedAmounts:', err);
      console.error('❌ Error response:', err.response);

      // Set user-friendly error message
      let errorMessage = 'Failed to load advance amounts';
      if (err.response?.status === 404) {
        errorMessage = 'advance amounts API endpoint not found';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error while loading advance amounts';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setAdvancedAmountsError(errorMessage);
      setAdvancedAmounts([]); // Always ensure it's an array

      // Refresh stats from API even if advance amounts failed to load
      await refreshAllStats();
    } finally {
      setAdvancedAmountsLoading(false);
    }
  };

  // Fetch owner payments function (extracted for reusability)
  const fetchOwnerPayments = useCallback(async () => {
    // Only fetch if not free plan and user is admin/superadmin
    if (!subscriptionStatus.loading && !subscriptionStatus.isFreePlan) {
      setOwnerPaymentsLoading(true);
      try {
        const response = await financialAPI.getAllOwnerPaymentsForAdmin();
        setOwnerPayments(response.data || []);
        setOwnerPaymentsError(null);
      } catch (err) {
        setOwnerPaymentsError('Failed to load owner payments');
        setOwnerPayments([]);
      } finally {
        setOwnerPaymentsLoading(false);
      }
    }
  }, [subscriptionStatus]);

  // Fetch owner payments for admin
  useEffect(() => {
    fetchOwnerPayments();
  }, [fetchOwnerPayments]);

  // Fetch advance amounts for admin
  useEffect(() => {
    // Only fetch if not free plan and user is admin/superadmin
    if (!subscriptionStatus.loading && !subscriptionStatus.isFreePlan) {
      console.log('⏰ Starting fetchAdvancedAmounts...');
      fetchAdvancedAmounts();
    }
  }, [subscriptionStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // If unit_id changed, reset advance amount states and fetch new balance
    if (name === 'unit_id') {
      // Always reset advance amount states when unit changes (including switching between units)
      // This prevents confusion when user switches between units with different advanced balances
      setUseAdvancedAmount(false);
      setAdvancedAmountToUse(null);
      setAdvancedAmountError(null);
      setPaymentBreakdown({ total: 0, advancedUsed: 0, cashPayment: 0 });

      if (value) {
        // Fetch new unit's advanced balance
        fetchUnitAdvancedBalance(value);

        // Auto-fill amount if maintenance category is selected
        if (formData.category === 'maintenance') {
          const selectedUnit = units.find(u => u.id.toString() === value);
          if (selectedUnit) {
            const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
            if (maintenanceFee > 0) {
              setFormData(prev => ({ ...prev, amount: maintenanceFee.toString() }));
            }
          }
        }
        
        // Auto-fill amount if transaction type is payment, category is booking, and advanced amount is used
        if (formData.type === 'payment' && formData.category === 'booking') {
          if (useAdvancedAmount && unitAdvancedBalance > 0) {
            // If advanced amount is used, set amount to advanced balance
            setFormData(prev => ({ ...prev, amount: unitAdvancedBalance.toString() }));
          } else {
            // If advanced amount is not used, leave amount field blank
            setFormData(prev => ({ ...prev, amount: '' }));
          }
          // For booking category, ensure status is always 'paid'
          if (formData.status !== 'paid') {
            setFormData(prev => ({ ...prev, status: 'paid' }));
          }
        }
      } else {
        // Clear balance when no unit selected
        setUnitAdvancedBalance(0);
      }
    }

    // If category changed to maintenance and unit is selected, auto-fill amount
    if (name === 'category' && value === 'maintenance' && formData.unit_id) {
      const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
      if (selectedUnit) {
        const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
        if (maintenanceFee > 0) {
          setFormData(prev => ({ ...prev, amount: maintenanceFee.toString() }));
        }
      }
    }
    
    // If category changed to booking and unit is selected, handle amount based on advanced amount
    if (name === 'category' && value === 'booking' && formData.type === 'payment' && formData.unit_id) {
      if (useAdvancedAmount && unitAdvancedBalance > 0) {
        // If advanced amount is used, set amount to advanced balance
        setFormData(prev => ({ ...prev, amount: unitAdvancedBalance.toString() }));
      } else {
        // If advanced amount is not used, leave amount field blank
        setFormData(prev => ({ ...prev, amount: '' }));
      }
      // For booking category, automatically set status to 'paid' if it's not already
      if (formData.status !== 'paid') {
        setFormData(prev => ({ ...prev, status: 'paid' }));
      }
    }

    // If category changed FROM maintenance to something else, clear any maintenance-related errors
    if (name === 'category' && formData.category === 'maintenance' && value !== 'maintenance') {
      setError(null);
    }
    
    // If category changed FROM booking to something else, clear advanced amount error
    if (name === 'category' && formData.category === 'booking' && value !== 'booking') {
      setAdvancedAmountError(null);
    }

    // If category changed FROM other_income to something else, clear unit_id (if it was selected for other_income)
    if (name === 'category' && formData.category === 'other_income' && value !== 'other_income') {
      // Only clear if switching to a category that doesn't use unit_id, or if switching to maintenance/booking
      // which have their own unit handling
      if (value !== 'maintenance' && value !== 'booking') {
        setFormData(prev => ({ ...prev, unit_id: '' }));
      }
    }

    // If type changed from payment to expense, clear unit_id if category was other_income
    if (name === 'type' && value === 'expense' && formData.type === 'payment' && formData.category === 'other_income') {
      setFormData(prev => ({ ...prev, unit_id: '', category: '' }));
    }

    // If category changed TO maintenance, validate existing amount
    if (formData.type === 'payment') {
      if (name === 'category' && value === 'maintenance' && formData.unit_id && formData.amount) {
        const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
        if (selectedUnit) {
          const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
          const currentAmount = parseFloat(formData.amount);

          // If current amount exceeds maintenance fee, limit it
          if (maintenanceFee > 0 && currentAmount > maintenanceFee) {
            setFormData(prev => ({ ...prev, amount: maintenanceFee.toString() }));
            setError(`Amount limited to maintenance fee of ₹${maintenanceFee.toLocaleString('en-IN')} for this unit.`);
            setTimeout(() => setError(null), 3000);
          }
        }
      }
    } else {
      if (name === 'category' && value === 'maintenance' && formData.unit_id && formData.amount) {
        const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
        if (selectedUnit) {
          const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
          const currentAmount = parseFloat(formData.amount);

          setFormData(prev => ({ ...prev, amount: currentAmount.toString() }));
        }
      }
    }

    // If amount changed, validate (but don't recalculate for partial payments as amount is read-only)
    if (name === 'amount') {
      // For maintenance category, prevent entering amount greater than maintenance fee (except for partial payments)
      if (formData.category === 'maintenance' && formData.unit_id && formData.status !== 'partially_paid') {
        const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
        if (selectedUnit) {
          const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
          const enteredAmount = parseFloat(value);

          // If user tries to enter more than maintenance fee, limit it to maintenance fee
          if (maintenanceFee > 0 && enteredAmount > maintenanceFee) {
            setFormData(prev => ({ ...prev, [name]: maintenanceFee.toString() }));
            setError(`Amount limited to maintenance fee of ₹${maintenanceFee.toLocaleString('en-IN')} for this unit.`);
            setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
            updatePaymentBreakdown(maintenanceFee, advancedAmountToUse, formData.payment_method);
            return;
          }
        }
      }

      // Only recalculate for non-partial payments or non-maintenance categories
      if (formData.status !== 'partially_paid' || formData.category !== 'maintenance') {
        updatePaymentBreakdown(parseFloat(value) || 0, advancedAmountToUse, formData.payment_method);
      }
    }

    // Handle partial_paid_amount changes
    if (name === 'partial_paid_amount') {
      const partialAmount = parseFloat(value) || 0;
      const totalAmount = parseFloat(formData.amount) || 0;
      const advancedUsed = parseFloat(advancedAmountToUse) || 0;

      // Calculate remaining amount based on payment status and advance usage
      if (formData.status === 'partially_paid') {
        // For partially_paid status
        if (useAdvancedAmount && advancedUsed > 0) {
          // Using advance amount
          const totalPaid = partialAmount + advancedUsed;
          const remainingAmount = Math.max(0, totalAmount - totalPaid);
          setFormData(prev => ({ ...prev, remaining_amount: remainingAmount.toFixed(2) }));
        } else {
          // Not using advance amount
          const remainingAmount = Math.max(0, totalAmount - partialAmount);
          setFormData(prev => ({ ...prev, remaining_amount: remainingAmount.toFixed(2) }));
        }
      } else if (formData.status === 'paid') {
        // For paid status
        if (useAdvancedAmount && advancedUsed > 0) {
          // Using advance amount
          const totalPaid = partialAmount + advancedUsed;
          const remainingAmount = Math.max(0, totalAmount - totalPaid);
          setFormData(prev => ({ ...prev, remaining_amount: remainingAmount.toFixed(2) }));
        } else {
          // Not using advance amount
          const remainingAmount = Math.max(0, totalAmount - partialAmount);
          setFormData(prev => ({ ...prev, remaining_amount: remainingAmount.toFixed(2) }));
        }
      }
    }

    // Payment method is now auto-determined, no manual updates needed

    // If payment status changed, recalculate payment breakdown (KEY FIELD)
    if (name === 'status') {
      console.log('🔄 Payment status changed:', value);

      if (value === 'partially_paid') {
        setFormData(prev => ({
          ...prev,
          payment_date: formData.payment_date ? formData.payment_date : "" // reset only if no DB value
        }));

        // Check if we have original values stored (from edit mode)
        if (originalPartiallyPaidValues.partial_paid_amount !== '') {
          // Restore original values for partially_paid status
          setFormData(prev => ({
            ...prev,
            partial_paid_amount: originalPartiallyPaidValues.partial_paid_amount,
            remaining_amount: originalPartiallyPaidValues.remaining_amount
          }));
          
          // Restore original advance amount settings
          setUseAdvancedAmount(originalPartiallyPaidValues.useAdvancedAmount);
          setAdvancedAmountToUse(originalPartiallyPaidValues.advancedAmountToUse);
        } else {
          // Auto-fill partial_paid_amount based on advance usage (for new transactions)
          const totalAmount = parseFloat(formData.amount) || 0;
          const advancedUsed = parseFloat(advancedAmountToUse) || 0;

          if (useAdvancedAmount && advancedUsed > 0) {
            // Using advance amount - calculate what needs to be paid in cash
            const cashNeeded = Math.max(0, totalAmount - advancedUsed);
            setFormData(prev => ({
              ...prev,
              partial_paid_amount: cashNeeded.toFixed(2),
              remaining_amount: '0.00' // No remaining if advance covers full amount
            }));
          } else {
            // Not using advance amount - auto-fill with total amount
            setFormData(prev => ({
              ...prev,
              partial_paid_amount: totalAmount.toFixed(2),
              remaining_amount: '0.00'
            }));
          }
        }

        // If changing to partial payment for maintenance, auto-fill with maintenance fee
        if (formData.category === 'maintenance' && formData.unit_id) {
          const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
          if (selectedUnit) {
            const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
            if (maintenanceFee > 0) {
              setFormData(prev => ({ ...prev, amount: maintenanceFee.toString() }));
              return; // Exit early since we're updating the amount
            }
          }
        }
      } else if (value === 'paid') {
        setFormData(prev => ({
          ...prev,
          partially_payment_date: formData.partially_payment_date ? formData.partially_payment_date : "" // reset only if no DB value
        }));

        // Check if we have original values stored (from edit mode)
        if (originalPartiallyPaidValues.partial_paid_amount !== '') {
          // For paid status, set advance amount to remaining amount and calculate cash payment
          const totalAmount = parseFloat(formData.amount) || 0;
          const remainingAmount = parseFloat(originalPartiallyPaidValues.remaining_amount) || 0;
          
          if (useAdvancedAmount) {
            // Set advance amount to remaining amount
            setAdvancedAmountToUse(remainingAmount);
            
            // Calculate cash payment
            const cashPayment = Math.max(0, totalAmount - remainingAmount);
            setFormData(prev => ({
              ...prev,
              partial_paid_amount: cashPayment.toFixed(2),
              remaining_amount: '0.00'
            }));
          } else {
            // Not using advance amount
            setFormData(prev => ({
              ...prev,
              partial_paid_amount: totalAmount.toFixed(2),
              remaining_amount: '0.00'
            }));
          }
        } else {
          // Auto-fill partial_paid_amount based on advance usage
          const totalAmount = parseFloat(formData.amount) || 0;
          const advancedUsed = parseFloat(advancedAmountToUse) || 0;

          if (useAdvancedAmount && advancedUsed > 0) {
            // Using advance amount - calculate what needs to be paid in cash
            const cashNeeded = Math.max(0, totalAmount - advancedUsed);
            setFormData(prev => ({
              ...prev,
              partial_paid_amount: cashNeeded.toFixed(2),
              remaining_amount: '0.00' // No remaining if advance covers full amount
            }));
          } else {
            // Not using advance amount - auto-fill with total amount
            setFormData(prev => ({
              ...prev,
              partial_paid_amount: totalAmount.toFixed(2),
              remaining_amount: '0.00'
            }));
          }
        }
      } else if (value === 'pending') {
        setFormData(prev => ({
          ...prev,
          partial_paid_amount: formData.partially_paid_amount ? formData.partially_paid_amount : "" // reset only if no DB value
        }));
      }
    }
  };

  // Handle file input change for expense documents
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Validate each file
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      
      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          setError(`File "${file.name}" is not a valid file type (JPG, JPEG, PNG, or PDF).`);
          e.target.value = '';
          return;
        }
        
        if (file.size > maxSize) {
          setError(`File "${file.name}" size must be less than 10MB.`);
          e.target.value = '';
          return;
        }
      }

      // Add new files to existing selected files
      setSelectedFiles(prev => [...prev, ...files]);
      setError(null);
    }
  };

  const handleRemoveDocument = (index = null) => {
    if (index !== null) {
      // Remove specific file by index
      setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      // Remove all files and clear document URLs
      setFormData(prev => ({ ...prev, document_url: [] }));
      setSelectedFiles([]);
      // Clear the file input
      const fileInput = document.getElementById('document');
      if (fileInput) {
        fileInput.value = '';
      }
    }
    setError(null);
  };

  // File handling for advance amount documents
  const handleAdvancedFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      for (const file of files) {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          setError(`File "${file.name}" must be JPG, JPEG, PNG, or PDF.`);
          e.target.value = '';
          return;
        }
        
        // Validate file size
        if (file.size > maxSize) {
          setError(`File "${file.name}" size must be less than 10MB.`);
          e.target.value = '';
          return;
        }
      }

      // Add new files to existing selected files
      setSelectedAdvancedFiles(prev => [...prev, ...files]);
      setError(null);
    }
  };

  const handleRemoveAdvancedDocument = (index = null) => {
    if (index !== null) {
      // Remove specific file by index
      setSelectedAdvancedFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      // Remove all files and clear document URLs
      setAdvancedFormData(prev => ({ ...prev, document_url: [] }));
      setSelectedAdvancedFiles([]);
      // Clear the file input
      const fileInput = document.getElementById('advanced_document');
      if (fileInput) {
        fileInput.value = '';
      }
    }
    setError(null);
  };

  // Validation function for maintenance fee amount
  const validateMaintenanceAmount = () => {

    if (formData.type === 'payment' && formData.category === 'maintenance' && formData.unit_id && formData.amount) {
      const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
      if (selectedUnit) {
        const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
        const enteredAmount = parseFloat(formData.amount);

        // For partial payments, allow full maintenance fee amount
        // For full payments, enforce the maintenance fee limit
        if (maintenanceFee > 0 && enteredAmount > maintenanceFee) {
          // Only show error if it's not a partial payment scenario
          if (formData.status !== 'partially_paid') {
            return {
              isValid: false,
              errorMessage: `Amount cannot exceed maintenance fee of ₹${maintenanceFee.toLocaleString('en-IN')} for this unit.`
            };
          }
        }
      }
    }
    return { isValid: true, errorMessage: '' };
  };

  // Fetch unit's advanced balance
  const fetchUnitAdvancedBalance = async (unitId) => {
    try {
      const response = await advancedAmountAPI.getByUnitId(unitId);
      if (response.data && response.data.success) {
        const advancedAmounts = response.data.data || [];

        // Calculate current balance (sum of credits minus debits)
        const balance = advancedAmounts.reduce((total, amount) => {
          return amount.advance_type === 'credit'
            ? total + parseFloat(amount.amount || 0)
            : total - parseFloat(amount.amount || 0);
        }, 0);

        setUnitAdvancedBalance(Math.max(0, balance)); // Don't allow negative balance usage
      }
    } catch (error) {
      console.error('Error fetching unit advanced balance:', error);
      setUnitAdvancedBalance(0);
    }
  };

  /*
   * PAYMENT BREAKDOWN CALCULATION
   * =============================
   * Handles all payment scenarios:
   * 1. Full Payment: advance + cash = total
   * 2. Partial Payment: advance + cash + remaining = total
   * 3. Auto-updates payment method based on actual payment breakdown
   */
  const recalculatePaymentBreakdown = useCallback(() => {
    const totalAmount = parseFloat(formData.amount) || 0;
    const remainingAmount = parseFloat(formData.remaining_amount) || 0;
    const advancedUsed = parseFloat(advancedAmountToUse) || 0;
    const isPartialPayment = formData.status === 'partially_paid';
    const isPaidStatus = formData.status === 'paid';
    const originalPartiallyPaidAmount = parseFloat(originalPartiallyPaidValues.partial_paid_amount) || 0;

    let finalAdvancedUsed = 0;
    let cashPayment = 0;
    let actualPaymentAmount = totalAmount; // Amount actually being paid now

    if (isPartialPayment && remainingAmount > 0) {
      // **PARTIAL PAYMENT SCENARIO**
      // Total Due: 2500, Remaining: 1000 → Actual Paid: 1500
      actualPaymentAmount = Math.max(0, totalAmount - remainingAmount);

      // Use advance amount only if checkbox is checked
      if (useAdvancedAmount && advancedUsed > 0) {
        finalAdvancedUsed = Math.min(advancedUsed, actualPaymentAmount, unitAdvancedBalance);
      } else {
        finalAdvancedUsed = 0;
      }

      // Cash payment = what's left after advance
      cashPayment = Math.max(0, actualPaymentAmount - finalAdvancedUsed);

    } else if (isPaidStatus && originalPartiallyPaidAmount > 0) {
      // **PAID STATUS WITH ORIGINAL PARTIALLY PAID AMOUNT SCENARIO**
      // Total Amount: 2600, Already Paid: 1000, Advance Used: 1200 → Cash Payment: 400
      actualPaymentAmount = totalAmount - originalPartiallyPaidAmount; // Amount being paid now

      // Use advance amount only if checkbox is checked
      if (useAdvancedAmount && advancedUsed > 0) {
        finalAdvancedUsed = Math.min(advancedUsed, actualPaymentAmount, unitAdvancedBalance);
      } else {
        finalAdvancedUsed = 0;
      }

      // Cash payment = what's left after advance (for the current payment)
      cashPayment = Math.max(0, actualPaymentAmount - finalAdvancedUsed);

    } else {
      // **FULL PAYMENT SCENARIO**
      actualPaymentAmount = totalAmount;

      // Use advance amount only if checkbox is checked
      if (useAdvancedAmount && advancedUsed > 0) {
        finalAdvancedUsed = Math.min(advancedUsed, totalAmount, unitAdvancedBalance);
      } else {
        finalAdvancedUsed = 0;
      }

      // Cash payment = what's left after advance
      cashPayment = Math.max(0, totalAmount - finalAdvancedUsed);
    }

    // **AUTO-UPDATE PAYMENT METHOD** based on actual payment breakdown
    let autoPaymentMethod = 'cash_payment'; // default

    if (actualPaymentAmount > 0) {
      if (finalAdvancedUsed === 0) {
        // No advance used - pure cash payment
        autoPaymentMethod = 'cash_payment';
      } else if (finalAdvancedUsed >= actualPaymentAmount) {
        // Full payment from advance
        autoPaymentMethod = 'from_advance_balance';
      } else {
        // Mixed payment: cash + advance
        autoPaymentMethod = 'cash_from_advance_balance';
      }
    }
    // Update payment method in form data
    setFormData(prev => ({ ...prev, payment_method: autoPaymentMethod }));

    const result = {
      total: totalAmount,
      advancedUsed: finalAdvancedUsed,
      cashPayment
    };

    setPaymentBreakdown(result);
  }, [formData.amount, formData.remaining_amount, formData.status, advancedAmountToUse, useAdvancedAmount, unitAdvancedBalance]);

  // Legacy function for backward compatibility - remove unused parameters
  const updatePaymentBreakdown = () => {
    recalculatePaymentBreakdown();
  };

  // Handle advance amount usage change (KEY FIELD)
  const handleAdvancedAmountChange = (e) => {
    // Just store the raw value, don't parse or validate yet
    const rawValue = e.target.value;
    
    console.log('🔄 Advance amount changed:', rawValue);

    // Update the advance amount state with raw value
    setAdvancedAmountToUse(rawValue);
    
    // Clear error when user starts typing (for booking category)
    if (formData.category === 'booking' && advancedAmountError) {
      setAdvancedAmountError(null);
    }

    // Parse the value for calculations
    const parsedValue = parseFloat(rawValue) || 0;
    
    // Validate for booking category: check if amount exceeds available balance
    if (formData.category === 'booking' && rawValue && rawValue.trim() !== '') {
      if (parsedValue > unitAdvancedBalance) {
        setAdvancedAmountError(`Cannot exceed available advance amount of ₹${unitAdvancedBalance.toFixed(2)}`);
      } else {
        setAdvancedAmountError(null);
      }
    }

    // Update amount field if transaction type is payment, category is booking, and unit is selected
    if (formData.type === 'payment' && formData.category === 'booking' && formData.unit_id) {
      // Set the amount field to the advanced amount used value
      setFormData(prev => ({
        ...prev,
        amount: rawValue.toString()
      }));
    }

    // Recalculate partial_paid_amount and remaining_amount when advance amount changes
    const totalAmount = parseFloat(formData.amount) || 0;
    const partialAmount = parseFloat(formData.partial_paid_amount) || 0;

    if (formData.status === 'partially_paid' || formData.status === 'paid') {
      if (useAdvancedAmount && parsedValue > 0) {
        // Using advance amount - calculate what needs to be paid in cash
        const cashNeeded = Math.max(0, totalAmount - parsedValue);
        const totalPaid = cashNeeded + parsedValue;
        const remainingAmount = Math.max(0, totalAmount - totalPaid);

        setFormData(prev => ({
          ...prev,
          partial_paid_amount: cashNeeded.toFixed(2),
          remaining_amount: remainingAmount.toFixed(2)
        }));

        // Note: Payment status will be changed manually by user selection
        // No automatic status change when using advance amount
      } else {
        // Not using advance amount - recalculate based on current partial amount
        const remainingAmount = Math.max(0, totalAmount - partialAmount);
        setFormData(prev => ({
          ...prev,
          remaining_amount: remainingAmount.toFixed(2)
        }));
      }
    }
  };

  // Validate and cap advance amount when user finishes typing (onBlur)
  const validateAdvancedAmount = () => {
    const parsedValue = parseFloat(advancedAmountToUse) || 0;
    
    // Calculate maximum allowed advance amount
    const transactionAmount = parseFloat(formData.amount) || 0;
    const remainingAmount = parseFloat(formData.remaining_amount) || 0;
    
    let maxAllowed = unitAdvancedBalance;
    if (formData.status === 'partially_paid' && remainingAmount > 0) {
      maxAllowed = Math.min(unitAdvancedBalance, remainingAmount);
    } else if (formData.status === 'paid' && remainingAmount > 0) {
      maxAllowed = Math.min(unitAdvancedBalance, remainingAmount);
    } else if (formData.status === 'paid' && remainingAmount === 0) {
      if (originalPartiallyPaidValues.remaining_amount !== '') {
        const originalRemaining = parseFloat(originalPartiallyPaidValues.remaining_amount) || 0;
        maxAllowed = Math.min(unitAdvancedBalance, originalRemaining);
      } else {
        maxAllowed = transactionAmount > 0
          ? Math.min(unitAdvancedBalance, transactionAmount)
          : unitAdvancedBalance;
      }
    } else {
      maxAllowed = transactionAmount > 0
        ? Math.min(unitAdvancedBalance, transactionAmount)
        : unitAdvancedBalance;
    }

    // Enforce the maximum limit
    if (parsedValue > maxAllowed) {
      setAdvancedAmountToUse(maxAllowed);
      setError(`Advance amount capped to maximum: ₹${maxAllowed.toFixed(2)}`);
      setTimeout(() => setError(null), 3000);
      
      // Trigger recalculation with capped value
      const syntheticEvent = {
        target: { value: maxAllowed.toString() }
      };
      handleAdvancedAmountChange(syntheticEvent);
    }
  };

  // Advance form input handler
  const handleAdvancedInputChange = (e) => {
    const { name, value } = e.target;
    setAdvancedFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler to edit advance amount
  const handleEditAdvanced = async (advancedAmount) => {
    try {
      // Fetch the full advance amount details
      const response = await advancedAmountAPI.getById(advancedAmount.id);
      const amountData = response.data.data;

      // Handle document_url - parse if it's a JSON string
      let documentUrls = [];
      if (amountData.document_url) {
        if (Array.isArray(amountData.document_url)) {
          documentUrls = amountData.document_url;
        } else if (typeof amountData.document_url === 'string') {
          try {
            const parsed = JSON.parse(amountData.document_url);
            documentUrls = Array.isArray(parsed) ? parsed : [parsed];
          } catch (e) {
            documentUrls = [amountData.document_url];
          }
        }
      }

      // Populate form with existing data
      setAdvancedFormData({
        unit_id: amountData.unit_id ? amountData.unit_id.toString() : '',
        amount: amountData.amount ? amountData.amount.toString() : '',
        payment_date: amountData.date ? amountData.date.toString().split('T')[0] : new Date().toISOString().split('T')[0],
        remarks: amountData.remarks || '',
        advance_type: amountData.advance_type || 'credit',
        document_url: documentUrls
      });

      // Set edit mode
      setIsEditAdvancedMode(true);
      setCurrentAdvancedId(advancedAmount.id);
      setSelectedAdvancedFiles([]);

      // Show the form
      setShowAdvancedForm(true);
    } catch (err) {
      console.error('Error loading advance amount for edit:', err);
      setError('Failed to load advance amount details. Please try again.');
    }
  };

  // Effect to trigger recalculation when key values change
  useEffect(() => {
    if (showForm && formData.type === 'payment' && formData.unit_id) {
      console.log('🔄 useEffect triggered - recalculating payment breakdown');
      recalculatePaymentBreakdown();
    }
  }, [recalculatePaymentBreakdown, showForm, formData.type, formData.unit_id]);

  // Effect to handle initial calculation of partial_paid_amount and remaining_amount
  useEffect(() => {
    if (showForm && formData.type === 'payment' && formData.unit_id && formData.amount) {
      const totalAmount = parseFloat(formData.amount) || 0;
      const advancedUsed = parseFloat(advancedAmountToUse) || 0;

      // Only calculate if status is partially_paid or paid and partial_paid_amount is empty
      if ((formData.status === 'partially_paid' || formData.status === 'paid') && !formData.partial_paid_amount) {
        if (useAdvancedAmount && advancedUsed > 0) {
          // Using advance amount - calculate what needs to be paid in cash
          const cashNeeded = Math.max(0, totalAmount - advancedUsed);
          const totalPaid = cashNeeded + advancedUsed;
          const remainingAmount = Math.max(0, totalAmount - totalPaid);

          setFormData(prev => ({
            ...prev,
            partial_paid_amount: cashNeeded.toFixed(2),
            remaining_amount: remainingAmount.toFixed(2)
          }));
        } else {
          // Not using advance amount - auto-fill with total amount
          setFormData(prev => ({
            ...prev,
            partial_paid_amount: totalAmount.toFixed(2),
            remaining_amount: '0.00'
          }));
        }
      }
    }
  }, [showForm, formData.type, formData.unit_id, formData.amount, formData.status, useAdvancedAmount, advancedAmountToUse]);

  // Edit transaction handler
  const handleEditTransaction = (transaction) => {
    console.log('=== EDIT TRANSACTION DEBUG ===');
    console.log('Full transaction object:', transaction);
    console.log('Transaction document_url:', transaction.document_url);
    console.log('Document URL type:', typeof transaction.document_url);
    console.log('Is array:', Array.isArray(transaction.document_url));
    if (transaction.document_url) {
      console.log('Document URL length:', transaction.document_url.length);
      console.log('Document URL stringified:', JSON.stringify(transaction.document_url));
    }
    
    // Handle both single URL and array formats dynamically
    let documentUrls = [];
    if (transaction.document_url) {
      if (Array.isArray(transaction.document_url)) {
        // Already an array
        documentUrls = transaction.document_url;
      } else if (typeof transaction.document_url === 'string') {
        // Check if it's a JSON string
        try {
          const parsed = JSON.parse(transaction.document_url);
          if (Array.isArray(parsed)) {
            documentUrls = parsed;
          } else {
            // Single URL
            documentUrls = [transaction.document_url];
          }
        } catch (e) {
          // Not JSON, treat as single URL
          documentUrls = [transaction.document_url];
        }
      }
    }
    
    console.log('Processed documentUrls:', documentUrls);
    console.log('=== END EDIT TRANSACTION DEBUG ===');
    
    setIsEditMode(true);
    setCurrentTransactionId(transaction.id);
    setFormData({
      type: transaction.type,
      amount: transaction.amount.toString(),
      category: transaction.category || '',
      month_id: transaction.month_id ? transaction.month_id.toString() : '',
      unit_id: transaction.unit_id ? transaction.unit_id.toString() : '',
      date: transaction.date,
      payment_date: transaction.payment_date ? transaction.payment_date.toString() : new Date().toISOString().split('T')[0],
      partially_payment_date: transaction.partially_payment_date ? transaction.partially_payment_date.toString() : '',
      partial_paid_amount: transaction.partially_paid_amount ? transaction.partially_paid_amount.toString() : '',
      already_partial_paid_amount: transaction.partially_paid_amount ? transaction.partially_paid_amount.toString() : '',
      remarks: transaction.remarks || '',
      status: transaction.status || 'paid',
      remaining_amount: transaction.remaining_amount ? transaction.remaining_amount.toString() : '',
      year: transaction.year ? transaction.year.toString() : new Date().getFullYear().toString(),
      payment_method: transaction.payment_method || 'cash_payment',
      document_url: documentUrls
    });

    // Set flag to indicate if partially_payment_date is from database
    setIsPartiallyPaymentDateFromDB(!!transaction.partially_payment_date);

    // Store original values for partially_paid transactions
    if (transaction.status === 'partially_paid') {
      setOriginalPartiallyPaidValues({
        partial_paid_amount: transaction.partially_paid_amount ? transaction.partially_paid_amount.toString() : '',
        remaining_amount: transaction.remaining_amount ? transaction.remaining_amount.toString() : '',
        useAdvancedAmount: transaction.use_advanced_amount || false,
        advancedAmountToUse: transaction.advanced_amount_used ? parseFloat(transaction.advanced_amount_used) : null
      });
    }

    // Reset advance amount states for edit mode, then load transaction's advance amount data
    setUseAdvancedAmount(false);
    setAdvancedAmountToUse(null);
    setAdvancedAmountError(null);
    setUnitAdvancedBalance(0);
    setPaymentBreakdown({ total: 0, advancedUsed: 0, cashPayment: 0 });

    // Reset file upload state for edit mode
    setSelectedFiles([]);

    // Fetch advanced balance if unit is selected
    if (transaction.unit_id) {
      fetchUnitAdvancedBalance(transaction.unit_id.toString());

      // Load existing advance amount usage if available
      setTimeout(() => {
        if (transaction.use_advanced_amount && transaction.advanced_amount_used) {
          setUseAdvancedAmount(true);
          setAdvancedAmountToUse(parseFloat(transaction.advanced_amount_used));
          updatePaymentBreakdown(
            parseFloat(transaction.amount),
            parseFloat(transaction.advanced_amount_used),
            transaction.payment_method
          );
        }
      }, 500); // Small delay to ensure advanced balance is fetched first
    }

    setShowForm(true);
  };

  // File handlers for document upload modal
  const handleDocumentFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setDocumentUploadFiles(files);
      setDocumentUploadError(null);
    }
  };

  const handleRemoveDocumentFile = (index) => {
    if (index !== undefined) {
      // Remove specific file
      const newFiles = documentUploadFiles.filter((_, i) => i !== index);
      setDocumentUploadFiles(newFiles);
    } else {
      // Remove all files
      setDocumentUploadFiles([]);
    }
  };

  // Document upload handler
  const handleUploadDocument = (transaction) => {
    console.log('=== DOCUMENT UPLOAD DEBUG ===');
    console.log('Transaction for document upload:', transaction);
    
    // Handle both single URL and array formats dynamically
    let documentUrls = [];
    if (transaction.document_url) {
      if (Array.isArray(transaction.document_url)) {
        // Already an array
        documentUrls = transaction.document_url;
      } else if (typeof transaction.document_url === 'string') {
        // Check if it's a JSON string
        try {
          const parsed = JSON.parse(transaction.document_url);
          if (Array.isArray(parsed)) {
            documentUrls = parsed;
          } else {
            // Single URL
            documentUrls = [transaction.document_url];
          }
        } catch (e) {
          // Not JSON, treat as single URL
          documentUrls = [transaction.document_url];
        }
      }
    }
    
    console.log('Existing documents:', documentUrls);
    console.log('=== END DOCUMENT UPLOAD DEBUG ===');
    
    setDocumentUploadTransaction(transaction);
    setDocumentUploadFiles([]);
    setDocumentUploadError(null);
    setDocumentUploadSuccess(null);
    setShowDocumentUploadModal(true);
  };

  // Submit handler for document upload
  const handleDocumentUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (documentUploadFiles.length === 0) {
      setDocumentUploadError('Please select at least one file to upload.');
      return;
    }
    
    try {
      // Get organization ID from user session
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const organizationId = userObj.organization_id || userObj.tenant_id;
      
      // Create FormData for multiple file upload
      const formDataForUpload = new FormData();
      documentUploadFiles.forEach((file, index) => {
        formDataForUpload.append('documents', file);
      });
      
      // Choose the appropriate endpoint based on transaction type
      const uploadEndpoint = documentUploadTransaction.type === 'expense' 
        ? '/api/financials/upload-expense-documents'
        : '/api/financials/upload-income-documents';
      
      // Upload files
      const uploadResponse = await api.post(uploadEndpoint, formDataForUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          organization_id: organizationId
        }
      });
      
      if (uploadResponse.data && uploadResponse.data.success) {
        // Extract document URLs from the response
        const uploadedUrls = uploadResponse.data.uploadedFiles.map(file => file.document_url);
        
        // Get existing documents
        let existingUrls = [];
        if (documentUploadTransaction.document_url) {
          if (Array.isArray(documentUploadTransaction.document_url)) {
            existingUrls = documentUploadTransaction.document_url;
          } else if (typeof documentUploadTransaction.document_url === 'string') {
            try {
              const parsed = JSON.parse(documentUploadTransaction.document_url);
              if (Array.isArray(parsed)) {
                existingUrls = parsed;
              } else {
                existingUrls = [documentUploadTransaction.document_url];
              }
            } catch (e) {
              existingUrls = [documentUploadTransaction.document_url];
            }
          }
        }
        
        // Combine existing and new document URLs
        const allDocumentUrls = [...existingUrls, ...uploadedUrls];
        
        // Update the transaction with new documents using the documents-only endpoint
        const updateData = {
          document_url: allDocumentUrls
        };
        
        await api.put(`/api/financials/${documentUploadTransaction.id}/documents`, updateData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setDocumentUploadSuccess(`Successfully uploaded ${uploadedUrls.length} document(s)`);
        setDocumentUploadFiles([]);
        
        // Refresh the transactions list
        fetchFinancialData();
        
        // Close modal after success
        setTimeout(() => {
          setShowDocumentUploadModal(false);
          setDocumentUploadTransaction(null);
        }, 1500);
        
      } else {
        throw new Error(uploadResponse.data?.message || 'Upload failed');
      }
      
    } catch (error) {
      console.error('Error uploading documents:', error);
      setDocumentUploadError('Failed to upload documents. Please try again.');
    }
  };

  function formatMonthYearFromDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date)) return '-';
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${month}, ${year}`;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate maintenance fee amount
    const validation = validateMaintenanceAmount();
    if (!validation.isValid) {
      setError(validation.errorMessage);
      return;
    }

    // Validate advanced amount for booking category
    if (formData.category === 'booking' && useAdvancedAmount && advancedAmountToUse) {
      const parsedValue = parseFloat(advancedAmountToUse) || 0;
      if (parsedValue > unitAdvancedBalance) {
        setAdvancedAmountError(`Cannot exceed available advance amount of ₹${unitAdvancedBalance.toFixed(2)}`);
        return;
      }
    }

    /*
     * DATABASE FIELDS BEING STORED:
     * =============================
     * 1. use_advanced_amount (Boolean): Whether the "Use advance amount" checkbox was checked
     * 2. advanced_amount_used (Number): The actual amount used from the unit's advanced balance
     * 3. cash_payment (Number): The cash portion of the payment (total - advanced_amount_used)
     * 4. payment_method (String): Selected payment method (cash_payment, from_advance_balance, cash_from_advance_balance)
     * 5. payment_breakdown (Object): Complete payment breakdown with total, advanced_used, cash_amount, payment_method
     * 
     * PAYMENT SCENARIOS:
     * ==================
     * - Scenario A: Full payment with advance amount (cash_payment = 0, advanced_amount_used = total)
     * - Scenario B: Partial payment with advance amount (cash_payment > 0, advanced_amount_used = available balance)
     * - Scenario C: Cash + from advance balance (cash_payment > 0, advanced_amount_used < available balance, user chosen)
     * - Regular payment: No advance amount used (cash_payment = total, advanced_amount_used = 0)
     */

    try {
      // Format amount as number
      const amount = parseFloat(formData.amount);
      const remainingAmount = parseFloat(formData.remaining_amount) || 0;
      const advancedUsed = useAdvancedAmount ? parseFloat(advancedAmountToUse) : 0;
      const isPartialPayment = formData.status === 'partially_paid';

      // Calculate cash payment using the SAME logic as recalculatePaymentBreakdown
      let cashPayment = 0;
      let finalAdvancedUsed = 0;

      if (isPartialPayment && remainingAmount > 0) {
        // **PARTIAL PAYMENT SCENARIO**
        // Total Due: 2500, Remaining: 1000 → Actual Paid: 1500
        const actualPaidAmount = Math.max(0, amount - remainingAmount);

        console.log('🏦 Database Save - Partial payment calculation:', {
          amount,
          remainingAmount,
          actualPaidAmount,
          advancedUsed
        });

        // Use advance amount only if checkbox is checked
        if (useAdvancedAmount && advancedUsed > 0) {
          finalAdvancedUsed = Math.min(advancedUsed, actualPaidAmount, unitAdvancedBalance);
        } else {
          finalAdvancedUsed = 0;
        }

        // Cash payment = what's left after advance
        cashPayment = Math.max(0, actualPaidAmount - finalAdvancedUsed);

      } else {
        // **FULL PAYMENT SCENARIO**
        // Use advance amount only if checkbox is checked
        if (useAdvancedAmount && advancedUsed > 0) {
          finalAdvancedUsed = Math.min(advancedUsed, amount, unitAdvancedBalance);
        } else {
          finalAdvancedUsed = 0;
        }

        // Cash payment = what's left after advance
        cashPayment = Math.max(0, amount - finalAdvancedUsed);
      }

      // **AUTO-DETERMINE PAYMENT METHOD** for database storage
      let autoPaymentMethod = 'cash_payment'; // default
      const actualPaymentAmount = isPartialPayment ? (amount - remainingAmount) : amount;

      if (actualPaymentAmount > 0) {
        if (finalAdvancedUsed === 0) {
          // No advance used - pure cash payment
          autoPaymentMethod = 'cash_payment';
        } else if (finalAdvancedUsed >= actualPaymentAmount) {
          // Full payment from advance
          autoPaymentMethod = 'from_advance_balance';
        } else {
          // Mixed payment: cash + advance
          autoPaymentMethod = 'cash_from_advance_balance';
        }
      }

      // Get unit details if unit_id is provided
      const selectedUnit = formData.unit_id ? units.find(u => u.id.toString() === formData.unit_id) : null;

      // Enhanced remarks to include payment method details
      let enhancedRemarks = formData.remarks || '';
      if (finalAdvancedUsed > 0) {
        const paymentDetails = `Payment: ₹${amount.toFixed(2)} (Advanced: ₹${finalAdvancedUsed.toFixed(2)}, Cash: ₹${cashPayment.toFixed(2)})`;
        enhancedRemarks = enhancedRemarks ? `${enhancedRemarks}\n${paymentDetails}` : paymentDetails;
      }

      // Ensure status is 'paid' for booking category
      const finalStatus = (formData.category === 'booking' && formData.type === 'payment') ? 'paid' : formData.status;

      // Create transaction data for API
      const transactionData = {
        type: formData.type,
        amount,
        category: formData.category,
        unit_id: formData.unit_id ? parseInt(formData.unit_id) : null,
        date: formData.date,
        payment_date: formData.payment_date,
        partially_payment_date: formData.partially_payment_date,
        partial_paid_amount: formData.partial_paid_amount,
        already_partial_paid_amount: formData.already_partial_paid_amount,
        month_id: formData.month_id ? parseInt(formData.month_id) : null,
        remarks: enhancedRemarks,
        status: finalStatus,
        remaining_amount: finalStatus === 'partially_paid' && formData.remaining_amount ? parseFloat(formData.remaining_amount) : null,
        year: formData.year ? parseInt(formData.year) : null,
        payment_method: autoPaymentMethod, // Use auto-determined payment method
        // Payment breakdown data for database storage
        use_advanced_amount: finalAdvancedUsed > 0, // Boolean: whether advance amount was actually used
        advanced_amount_used: finalAdvancedUsed,     // Number: actual amount used from advanced balance
        cash_payment: cashPayment,                   // Number: cash portion of the payment
        payment_breakdown: {                         // Object: complete payment breakdown for reference
          total: amount,
          advanced_used: finalAdvancedUsed,
          cash_amount: cashPayment,
          payment_method: autoPaymentMethod // Use auto-determined payment method
        }
      };

      // Handle file upload for expense, income transactions and booking category
      if ((formData.type === 'expense' || formData.type === 'payment' || (formData.category === 'booking' && formData.unit_id)) && selectedFiles.length > 0) {
        try {
          // Get organization ID from user session
          const userStr = sessionStorage.getItem('user');
          const userObj = userStr ? JSON.parse(userStr) : {};
          const organizationId = userObj.organization_id || userObj.tenant_id;

          // Create FormData for multiple file upload
          const formDataForUpload = new FormData();
          selectedFiles.forEach((file, index) => {
            formDataForUpload.append('documents', file);
          });

          // Upload all files at once using the appropriate multiple file endpoint
          const uploadEndpoint = formData.type === 'expense' 
            ? '/api/financials/upload-expense-documents'
            : '/api/financials/upload-income-documents';
          
          const uploadResponse = await api.post(uploadEndpoint, formDataForUpload, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              organization_id: organizationId
            }
          });

          if (uploadResponse.data && uploadResponse.data.success) {
            // Extract document URLs from the response
            const uploadedUrls = uploadResponse.data.uploadedFiles.map(file => file.document_url);
            
            // Combine new uploads with existing document URLs
            const existingUrls = Array.isArray(formData.document_url) ? formData.document_url : (formData.document_url ? [formData.document_url] : []);
            transactionData.document_url = [...existingUrls, ...uploadedUrls];
            
            console.log('✅ Files uploaded successfully:', uploadedUrls);
            console.log('🔍 All document URLs:', transactionData.document_url);
            
            // Show warnings for any errors
            if (uploadResponse.data.errors && uploadResponse.data.errors.length > 0) {
              console.warn('⚠️ Some files had upload issues:', uploadResponse.data.errors);
            }
          } else {
            console.warn('⚠️ File upload response missing success flag:', uploadResponse.data);
            throw new Error(uploadResponse.data.message || 'Upload failed');
          }
        } catch (uploadError) {
          console.error('❌ Error uploading files:', uploadError);
          // Don't fail the transaction, but warn the user
          setError('Transaction saved but file upload failed. Please try uploading the documents again.');
        }
      } else if ((formData.type === 'expense' || formData.type === 'payment' || (formData.category === 'booking' && formData.unit_id)) && selectedFiles.length === 0 && formData.document_url && formData.document_url.length > 0) {
        // Preserve existing document URLs when editing without uploading new files
        transactionData.document_url = Array.isArray(formData.document_url) ? formData.document_url : [formData.document_url];
        console.log('📄 Preserving existing document URLs:', transactionData.document_url);
      }

      // Log the complete transaction data being sent to API
      console.log('Transaction data to be saved:', transactionData);
      console.log('Payment breakdown details:', {
        use_advanced_amount: finalAdvancedUsed > 0,
        advanced_amount_used: finalAdvancedUsed,
        cash_payment: cashPayment,
        payment_method: autoPaymentMethod, // Use auto-determined payment method
        total_amount: amount
      });

      let response;

      if (isEditMode) {
        // Update existing transaction
        response = await financialAPI.updateFinancial(currentTransactionId, transactionData);
        // Handle advance amount usage for edited transactions
        console.log('🔍 Checking if advance amount should be debited for edit:', { finalAdvancedUsed, condition: finalAdvancedUsed > 0 });
        if (finalAdvancedUsed > 0) {
          const debitEntryData = {
            financial_id: currentTransactionId,
            unit_id: parseInt(formData.unit_id),
            amount: finalAdvancedUsed,
            payment_date: formData.date,
            remarks: `Used for updated transaction #${currentTransactionId} - ${formData.category || 'Payment'}`
          };

          try {
            console.log('🔄 Creating or updating debit entry for edited transaction:', debitEntryData);
            const debitResult = await advancedAmountAPI.createOrUpdateDebitEntry(debitEntryData);
            console.log('✅ Debit entry created/updated successfully:', debitResult);
            // Refresh advance amounts data to show the updated balance
            await fetchAdvancedAmounts();
          } catch (debitError) {
            // Don't fail the transaction, but warn the user
            setError('Transaction updated but failed to update advance amount balance. Please check manually.');
            console.error('❌ Error updating debit entry:', debitError);
            console.error('❌ Debit entry data that failed:', debitEntryData);
          }
        }

        // Set success message for update
        let successMsg = 'Transaction updated successfully!';
        if (finalAdvancedUsed > 0) {
          successMsg += ` (Used ₹${finalAdvancedUsed.toFixed(2)} from advanced balance)`;
        }
        setSuccessMessage(successMsg);

        // Add a small delay to ensure database changes are committed
        await new Promise(resolve => setTimeout(resolve, 800));

        // Show loading state during refresh
        setLoading(true);

        // Refresh transaction data and financial summary
        await fetchFinancialData();
        await refreshAllStats();

        // Hide loading state
        setLoading(false);
      } else {
        // Call API to create transaction
        response = await financialAPI.createFinancial(transactionData);

        // If advance amount was used, create a debit entry
        console.log('🔍 Checking if advance amount should be debited:', { finalAdvancedUsed, condition: finalAdvancedUsed > 0 });
        if (finalAdvancedUsed > 0) {
          const debitEntryData = {
            financial_id: response.data.financialId,
            unit_id: parseInt(formData.unit_id),
            amount: finalAdvancedUsed,
            payment_date: formData.date,
            remarks: `Used for transaction #${response.data.financialId} - ${formData.category || 'Payment'}`
          };

          try {
            console.log('🔄 Creating debit entry for new transaction:', debitEntryData);
            const debitResult = await advancedAmountAPI.createOrUpdateDebitEntry(debitEntryData);
            console.log('✅ Debit entry created successfully:', debitResult);
          } catch (debitError) {
            console.error('❌ Failed to create debit entry:', debitError);
            console.error('❌ Debit entry data that failed:', debitEntryData);
            // Don't fail the transaction, but warn the user
            setError('Transaction created but failed to update advance amount balance. Please check manually.');
          }
        }

        // Set success message for creation
        let successMsg = 'Transaction created successfully!';
        if (finalAdvancedUsed > 0) {
          successMsg += ` (Used ₹${finalAdvancedUsed.toFixed(2)} from advanced balance)`;
        }
        setSuccessMessage(successMsg);

        // Add a small delay to ensure database changes are committed
        await new Promise(resolve => setTimeout(resolve, 800));

        // Show loading state during refresh
        setLoading(true);

        // Refresh transaction data and financial summary
        await fetchFinancialData();
        await refreshAllStats();

        // Hide loading state
        setLoading(false);
      }

      // Reset error if any
      setError(null);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);

      // Reset form
      setFormData({
        type: 'payment',
        amount: '',
        category: '',
        unit_id: '',
        month_id: '',
        date: new Date().toISOString().split('T')[0],
        payment_date: new Date().toISOString().split('T')[0],
        partially_payment_date: '',
        partial_paid_amount: '',
        already_partial_paid_amount: '',
        remarks: '',
        status: 'paid',
        remaining_amount: '',
        year: new Date().getFullYear().toString(),
        payment_method: 'cash_payment',
        document_url: []
      });

      // Reset advance amount states
      setUseAdvancedAmount(false);
      setAdvancedAmountToUse(null);
      setAdvancedAmountError(null);
      setUnitAdvancedBalance(0);
      setPaymentBreakdown({ total: 0, advancedUsed: 0, cashPayment: 0 });

      // Reset the flag for partially_payment_date
      setIsPartiallyPaymentDateFromDB(false);

      // Reset original partially paid values
      setOriginalPartiallyPaidValues({
        partial_paid_amount: '',
        remaining_amount: '',
        useAdvancedAmount: false,
        advancedAmountToUse: null
      });

      // Reset file upload state
      setSelectedFiles([]);

      setShowForm(false);
      setIsEditMode(false);
      setCurrentTransactionId(null);
    } catch (err) {
      console.error('Error saving transaction:', err);
      console.error('Error deleting transaction:', err);
      const errorMessage = err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} transaction. Please try again.`;
      setError(errorMessage);

      // Make sure to turn off loading state in case of error
      setLoading(false);
    }
  };

  // Submit handler for advance amount
  const handleAdvancedSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      
      // Format amount as number
      const amount = parseFloat(advancedFormData.amount);

      // Get organization ID from user session
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const organizationId = userObj.organization_id || userObj.tenant_id;

      // Handle file upload for advance amounts
      let documentUrls = [];
      if (selectedAdvancedFiles.length > 0) {
        try {
          // Create FormData for multiple file upload
          const formDataForUpload = new FormData();
          selectedAdvancedFiles.forEach((file, index) => {
            formDataForUpload.append('documents', file);
          });

          // Upload all files at once using the multiple file endpoint
          const uploadResponse = await api.post('/api/advanced-amounts/upload-documents', formDataForUpload, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              organization_id: organizationId
            }
          });

          if (uploadResponse.data.success && uploadResponse.data.document_urls) {
            console.log('✅ Files uploaded successfully:', uploadResponse.data.document_urls);
            documentUrls = uploadResponse.data.document_urls;
            
            if (uploadResponse.data.errors && uploadResponse.data.errors.length > 0) {
              console.warn('⚠️ Some files had upload issues:', uploadResponse.data.errors);
            }
          } else {
            console.warn('⚠️ File upload response missing success flag:', uploadResponse.data);
          }
        } catch (uploadError) {
          console.error('❌ Error uploading files:', uploadError);
          setError('Advance amount saved but file upload failed. Please try uploading the documents again.');
        }
      } else if (selectedAdvancedFiles.length === 0 && advancedFormData.document_url && advancedFormData.document_url.length > 0) {
        // If no new files selected but existing documents are present (edit mode)
        documentUrls = Array.isArray(advancedFormData.document_url) 
          ? advancedFormData.document_url 
          : (typeof advancedFormData.document_url === 'string' ? JSON.parse(advancedFormData.document_url) : []);
      }

      // Create advance amount data
      const advancedAmountData = {
        unit_id: parseInt(advancedFormData.unit_id),
        amount,
        advance_type: advancedFormData.advance_type,
        payment_date: advancedFormData.payment_date,
        remarks: advancedFormData.remarks,
        document_url: documentUrls.length > 0 ? documentUrls : null
      };

      // Call API to create or update advance amount
      if (isEditAdvancedMode && currentAdvancedId) {
        await advancedAmountAPI.update(currentAdvancedId, advancedAmountData);
        setSuccessMessage('Advance amount updated successfully!');
      } else {
        await advancedAmountAPI.create(advancedAmountData);
        setSuccessMessage('Advance amount added successfully!');
      }

      // Reset error if any
      setError(null);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);

      // Reset form
      setAdvancedFormData({
        unit_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        remarks: '',
        advance_type: 'credit',
        document_url: []
      });
      
      // Reset edit mode
      setIsEditAdvancedMode(false);
      setCurrentAdvancedId(null);
      setSelectedAdvancedFiles([]);

      setShowAdvancedForm(false);

      // Add a small delay to ensure database changes are committed
      await new Promise(resolve => setTimeout(resolve, 800));

      // Refresh advance amounts and financial summary
      await fetchAdvancedAmounts();
      await refreshAllStats();

      // Hide loading state
      setLoading(false);

    } catch (err) {
      console.error('Error adding/updating advance amount:', err);
      const errorMessage = err.response?.data?.message || `Failed to ${isEditAdvancedMode ? 'update' : 'add'} advance amount. Please try again.`;
      setError(errorMessage);

      // Make sure to turn off loading state in case of error
      setLoading(false);
    }
  };

  function formatDateToDDMMYYYY(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function formatDateTime(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  function formatDateIST(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day));

      // Format to DD/MM/YYYY in IST timezone
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Delete handler for owner payments
  const handleDeleteOwnerPayment = async () => {
    if (!ownerPaymentToDelete) return;
    try {
      await financialAPI.deleteOwnerPayment(ownerPaymentToDelete.id);

      // Refresh owner payments data after successful deletion
      await fetchOwnerPayments();

      setShowDeleteOwnerPayment(false);
      setOwnerPaymentToDelete(null);
      setSuccessMessage('Owner payment deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete owner payment.');
      setShowDeleteOwnerPayment(false);
      setOwnerPaymentToDelete(null);
    }
  };

  // Bank balance functions
  const fetchLatestBankBalance = async () => {
    try {
      const response = await bankBalanceAPI.getLatestBalance();
      if (response.success) {
        setLatestBankBalance(response.data);
      }
    } catch (error) {
      console.error('Error fetching latest bank balance:', error);
    }
  };

  const handleBankBalanceInputChange = (e) => {
    const { name, value } = e.target;
    setBankBalanceFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenBankBalanceModal = (editData = null) => {
    if (editData) {
      setIsEditBankBalance(true);
      setCurrentBankBalanceId(editData.id);
      setBankBalanceFormData({
        amount: editData.amount.toString(),
        date: editData.date,
        description: editData.description || ''
      });
    } else {
      setIsEditBankBalance(false);
      setCurrentBankBalanceId(null);
      setBankBalanceFormData({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: ''
      });
    }
    setShowBankBalanceModal(true);
    setBankBalanceError(null);
    setBankBalanceSuccess(null);
  };

  const handleCloseBankBalanceModal = () => {
    setShowBankBalanceModal(false);
    setIsEditBankBalance(false);
    setCurrentBankBalanceId(null);
    setBankBalanceFormData({
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
    setBankBalanceError(null);
    setBankBalanceSuccess(null);
  };

  const handleBankBalanceSubmit = async (e) => {
    e.preventDefault();
    setBankBalanceLoading(true);
    setBankBalanceError(null);
    setBankBalanceSuccess(null);

    try {
      const formData = {
        amount: parseFloat(bankBalanceFormData.amount),
        date: bankBalanceFormData.date,
        description: bankBalanceFormData.description
      };

      let response;
      if (isEditBankBalance) {
        response = await bankBalanceAPI.update(currentBankBalanceId, formData);
      } else {
        response = await bankBalanceAPI.create(formData);
      }

      if (response.success) {
        setBankBalanceSuccess(
          isEditBankBalance
            ? 'Bank balance updated successfully!'
            : 'Bank balance added successfully!'
        );

        // Refresh bank balance data
        await fetchLatestBankBalance();

        // Close modal after a short delay
        setTimeout(() => {
          handleCloseBankBalanceModal();
        }, 1500);
      }
    } catch (error) {
      setBankBalanceError(
        error.response?.data?.message ||
        (isEditBankBalance ? 'Failed to update bank balance.' : 'Failed to add bank balance.')
      );
    } finally {
      setBankBalanceLoading(false);
    }
  };



  // Load bank balance data on component mount
  useEffect(() => {
    fetchLatestBankBalance();
    setIsBalanceLoaded(true);
  }, []);

  if (loading && subscriptionStatus.loading) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading financial data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show upgrade message for Free plan users
  if (subscriptionStatus.isFreePlan) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-title">
            <i className="fas fa-money-bill-wave"></i> Financial Management
          </h1>
        </div>

        <div className="card shadow-sm">
          <div className="card-body text-center p-5">
            <i className="fas fa-lock text-secondary mb-3" style={{ fontSize: '3rem' }}></i>
            <h3>Financial Features Locked</h3>
            <p className="text-muted mb-4">
              Financial management features are only available on the Pro plan.
              Upgrade your subscription to access detailed financial tracking, reporting and management.
            </p>

            <div className="row justify-content-center mb-4">
              <div className="col-md-6">
                <div className="card bg-light">
                  <div className="card-body">
                    <h5 className="card-title">Pro Plan Features:</h5>
                    <ul className="list-unstyled text-start">
                      <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Financial transaction tracking</li>
                      <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Income and expense management</li>
                      <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Financial reporting and analytics</li>
                      <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Payment tracking for residents</li>
                      <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Financial summary dashboards</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <a href="/admin/membership" className="btn btn-primary btn-lg">
              <i className="fas fa-arrow-up me-2"></i> Upgrade to Pro Plan
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 activity-container">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h1 className="page-title">
          <i className="fas fa-money-bill-wave"></i> Financial Management
        </h1>
        <div className="d-flex flex-wrap gap-2">
          {isBalanceLoaded && (() => {
            if (!latestBankBalance) {
              return (
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenBankBalanceModal()}
                >
                  <i className="fas fa-plus me-2"></i>
                  Add Bank Balance
                </button>
              );
            }

            const createdTime = new Date(latestBankBalance.created_at);
            const now = new Date();
            const diffInMinutes = (now - createdTime) / 60000;

            if (diffInMinutes <= 30) {
              return (
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenBankBalanceModal(latestBankBalance)}
                >
                  <i className="fas fa-edit me-1"></i>
                  Update Balance
                </button>
              );
            }
            return null;
          })()}

          <button
            className="btn btn-primary"
            onClick={() => {
              if (showForm && isEditMode) {
                setIsEditMode(false);
                setCurrentTransactionId(null);
                setFormData({
                  type: 'payment',
                  amount: '',
                  category: '',
                  unit_id: '',
                  month_id: '',
                  date: new Date().toISOString().split('T')[0],
                  payment_date: new Date().toISOString().split('T')[0],
                  partially_payment_date: '',
                  partial_paid_amount: '',
                  already_partial_paid_amount: '',
                  remarks: '',
                  status: 'paid',
                  remaining_amount: '',
                  year: new Date().getFullYear().toString()
                });
                // Reset original partially paid values when starting new transaction
                setOriginalPartiallyPaidValues({
                  partial_paid_amount: '',
                  remaining_amount: '',
                  useAdvancedAmount: false,
                  advancedAmountToUse: null
                });
              }
              
              // Reset original partially paid values when opening form for new transaction
              if (!showForm) {
                setOriginalPartiallyPaidValues({
                  partial_paid_amount: '',
                  remaining_amount: '',
                  useAdvancedAmount: false,
                  advancedAmountToUse: null
                });
                
                // Reset advance amount states for new transaction
                setUseAdvancedAmount(false);
                setAdvancedAmountToUse(null);
                setUnitAdvancedBalance(0);
                setPaymentBreakdown({ total: 0, advancedUsed: 0, cashPayment: 0 });
              }
              
              setShowForm(!showForm);
              setError(null);
            }}
          >
            <i className={`fas fa-plus me-2`}></i>
            {'Add Transaction'}
          </button>
          <button
            className="btn btn-success"
            onClick={() => {
              setShowAdvancedForm(!showAdvancedForm);
              setError(null);
            }}
          >
            <i className="fas fa-hand-holding-usd me-2"></i>
            Add advance amount
          </button>

        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} aria-label="Close"></button>
        </div>
      )}

      {/* Data Refresh Indicator */}
      {loading && (
        <div className="alert alert-info d-flex align-items-center" role="alert">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Refreshing financial data...</span>
        </div>
      )}

      {/* Financial Summary Stats */}
      <div className="financial-stats-container">
        <div className="financial-stat-card income">
          <div className="stat-title">TOTAL INCOME</div>
          <div className="stat-value" title={safeFormatCurrency(stats.totalIncome)}>
            {safeFormatCurrency(stats.totalIncome)}
          </div>
          <div className="stat-description">
            All time earnings
          </div>
        </div>

        <div className="financial-stat-card expenses">
          <div className="stat-title">TOTAL EXPENSES</div>
          <div className="stat-value" title={safeFormatCurrency(stats.totalExpenses)}>
            {safeFormatCurrency(stats.totalExpenses)}
          </div>
          <div className="stat-description">
            All recorded expenses
          </div>
        </div>

        <div className="financial-stat-card balance">
          <div className="stat-title">CURRENT BALANCE</div>
          <div className="stat-value" title={safeFormatCurrency(stats.balance)}>
            {safeFormatCurrency(stats.balance)}
          </div>
          <span className='badge rounded-pill bg-success py-2 mb-2'>
            +Advance {safeFormatCurrency(advancedPaymentsTotal)}
          </span>
          <div className="stat-description">
            Net balance available
          </div>
        </div>

        <div className="financial-stat-card pending clickable-card" onClick={handlePendingPaymentsClick}>
          <div className="stat-title">PENDING PAYMENTS</div>
          <div className="stat-value" title={safeFormatCurrency(stats.pendingPayments)}>
            {safeFormatCurrency(stats.pendingPayments)}
          </div>
          <div className="stat-description">
            Outstanding dues to collect
            <small className="d-block text-muted mt-1">
              (Includes pending + remaining amounts)
            </small>
            <div className="click-hint">
              <i className="fas fa-mouse-pointer me-1"></i>Click to filter
            </div>
          </div>
        </div>
      </div>

      {/* Financials Tabs Bar */}
      <div className="financials-tabs-bar mb-4">
        <button
          className={`financials-tab-btn${activeTab === 'all' ? ' active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Transactions
        </button>
        <button
          className={`financials-tab-btn${activeTab === 'income' ? ' active' : ''}`}
          onClick={() => setActiveTab('income')}
        >
          Income
        </button>
        <button
          className={`financials-tab-btn${activeTab === 'expenses' ? ' active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses
        </button>
        <button
          className={`financials-tab-btn${activeTab === 'advanced' ? ' active' : ''}`}
          onClick={() => setActiveTab('advanced')}
        >
          Advance Payments
        </button>
      </div>

      {/* Filters Section - Only show for transaction tabs */}
      {activeTab !== 'advanced' && (
        <div className="card filters-card shadow-sm mb-4">

          <div className="card-body">

            <div className="row g-3 align-items-end">

              <div className="col-lg-2 col-md-3">

                <label htmlFor="searchFilter" className="form-label filter-label">

                  <i className="fas fa-search me-1"></i>Search

                </label>

                <input

                  type="text"

                  className="form-control"

                  id="searchFilter"

                  placeholder="Search transactions..."

                  value={filters.search}

                  onChange={(e) => handleFilterChange('search', e.target.value)}

                />

              </div>



              <div className="col-lg-2 col-md-3">

                <label htmlFor="categoryFilter" className="form-label filter-label">

                  <i className="fas fa-tags me-1"></i>Category

                </label>

                <select

                  className="form-select"

                  id="categoryFilter"

                  value={filters.category}

                  onChange={(e) => handleFilterChange('category', e.target.value)}

                >

                  <option value="all">All Categories</option>

                  {getUniqueCategories().map(category => (

                    <option key={category} value={category}>

                      {category.replace('_', ' ').split(' ').map(word =>

                        word.charAt(0).toUpperCase() + word.slice(1)

                      ).join(' ')}

                    </option>

                  ))}

                </select>

              </div>



              <div className="col-lg-2 col-md-3">

                <label htmlFor="unitFilter" className="form-label filter-label">

                  <i className="fas fa-building me-1"></i>{unitSingular}

                </label>

                <select

                  className="form-select"

                  id="unitFilter"

                  value={filters.unit_id}

                  onChange={(e) => handleFilterChange('unit_id', e.target.value)}

                >

                  <option value="all">All {unitSingular}s</option>

                  {units && units.length > 0 ? (

                    units

                      .sort((a, b) => {

                        // First sort by unit_type

                        if (a.unit_type && b.unit_type) {

                          if (a.unit_type < b.unit_type) return -1;

                          if (a.unit_type > b.unit_type) return 1;

                        } else if (a.unit_type) return -1;

                        else if (b.unit_type) return 1;



                        // Then sort by villa_number

                        return a.villa_number.localeCompare(b.villa_number, undefined, { numeric: true });

                      })

                      .map(unit => (

                        <option key={unit.id} value={unit.id}>

                          {unit.unit_type ? `${unit.unit_type} ` : ''}{unit.villa_number} ({unit.owner_name})

                        </option>

                      ))

                  ) : (

                    <option disabled>No units available</option>

                  )}

                </select>

              </div>



              <div className="col-lg-2 col-md-3">

                <label htmlFor="statusFilter" className="form-label filter-label">

                  <i className="fas fa-circle me-1"></i>Status

                </label>

                <select

                  className="form-select"

                  id="statusFilter"

                  value={filters.status}

                  onChange={(e) => handleFilterChange('status', e.target.value)}

                >

                  <option value="all">All Statuses</option>

                  {getUniqueStatuses().map(status => (

                    <option key={status} value={status}>

                      {status.replace('_', ' ').split(' ').map(word =>

                        word.charAt(0).toUpperCase() + word.slice(1)

                      ).join(' ')}

                    </option>

                  ))}

                </select>

              </div>



              <div className="col-lg-2 col-md-3">

                <label htmlFor="monthFilter" className="form-label filter-label">

                  <i className="fas fa-calendar-alt me-1"></i>Month

                </label>

                <select

                  className="form-select"

                  id="monthFilter"

                  value={filters.month_id}

                  onChange={(e) => handleFilterChange('month_id', e.target.value)}

                >

                  <option value="all">All Months</option>

                  {getUniqueMonths().map(monthId => (

                    <option key={monthId} value={monthId}>

                      {monthNames[monthId]}

                    </option>

                  ))}

                </select>

              </div>



              <div className="col-lg-2 col-md-3">

                <label htmlFor="yearFilter" className="form-label filter-label">

                  <i className="fas fa-calendar me-1"></i>Year

                </label>

                <select

                  className="form-select"

                  id="yearFilter"

                  value={filters.year}

                  onChange={(e) => handleFilterChange('year', e.target.value)}

                >

                  <option value="all">All Years</option>

                  {getUniqueYears().map(year => (

                    <option key={year} value={year}>

                      {year}

                    </option>

                  ))}

                </select>

              </div>

            </div>



            <div className="row mt-3">

              <div className="col-12">

                <div className="d-flex gap-2 align-items-center justify-content-between">

                  <button

                    className="btn btn-outline-secondary clear-filters-btn searchCloseFilter"

                    onClick={resetFilters}

                    title="Clear all filters"

                  >

                    <i className="fas fa-times me-1"></i>Clear Filters

                  </button>

                  <div className="results-count">

                    {filteredTransactions.length} results

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
      )}

      {/* Transaction Form Modal */}
      {showForm && (
        <div className="modal fade show add-edit-transaction-modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg" style={{ maxWidth: '1200px' }}>
            <div className="modal-content">
              <div className={`modal-header ${themeClassName}`}>
                <h5 className="modal-title">
                  <i className={`fas fa-${isEditMode ? 'edit' : 'plus-circle'} me-2`}></i>
                  {isEditMode ? 'Edit Transaction' : 'Add New Transaction'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowForm(false);
                    setIsEditMode(false);
                    setCurrentTransactionId(null);
                    setFormData({
                      type: 'payment',
                      amount: '',
                      category: '',
                      unit_id: '',
                      month_id: '',
                      date: new Date().toISOString().split('T')[0],
                      payment_date: new Date().toISOString().split('T')[0],
                      partially_payment_date: '',
                      partial_paid_amount: '',
                      already_partial_paid_amount: '',
                      remarks: '',
                      status: 'paid',
                      remaining_amount: '',
                      year: new Date().getFullYear().toString(),
                      payment_method: 'cash_payment'
                    });
                    // Reset advance amount states
                    setUseAdvancedAmount(false);
                    setAdvancedAmountToUse(null);
                    setUnitAdvancedBalance(0);
                    setPaymentBreakdown({ total: 0, advancedUsed: 0, cashPayment: 0 });
                    // Reset the flag for partially_payment_date
                    setIsPartiallyPaymentDateFromDB(false);
                    // Reset original partially paid values
                    setOriginalPartiallyPaidValues({
                      partial_paid_amount: '',
                      remaining_amount: '',
                      useAdvancedAmount: false,
                      advancedAmountToUse: null
                    });
                  }}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              {/* Error Message */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show mx-3 mb-0" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                  <button type="button" className="btn-close" onClick={() => {
                    setError(null);
                    // Clear timeout if user dismisses the error message
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                      timeoutRef.current = null;
                    }
                  }} aria-label="Close"></button>
                </div>
              )}

              <div className="modal-body">
                <form onSubmit={handleSubmit} id="transactionForm">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className={`${isEditMode && formData.type === 'payment' && formData.category === 'maintenance' ? 'd-none' : ''} mb-3`}>
                        <label htmlFor="type" className="form-label">Transaction Type</label>
                        <select
                          className="form-select"
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="payment">Income / Payment</option>
                          <option value="expense">Expense</option>
                        </select>
                      </div>

                      <div className={`${isEditMode && formData.type === 'payment' && formData.category === 'maintenance' ? 'd-none' : ''} mb-3`}>
                        <label htmlFor="category" className="form-label">Category</label>
                        <select
                          className="form-select"
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Category</option>
                          {formData.type === 'payment' ? (
                            <>
                              <option value="maintenance">Maintenance Fee</option>
                              <option value="penalty">Penalty Fee</option>
                              <option value="booking">Common Area Booking</option>
                              <option value="other_income">Other Income</option>
                            </>
                          ) : (
                            <>
                              <option value="utilities">Utilities</option>
                              <option value="maintenance">Maintenance</option>
                              <option value="staff">Staff Salary</option>
                              <option value="repairs">Repairs</option>
                              <option value="other_expense">Other Expense</option>
                            </>
                          )}
                        </select>
                      </div>

                      {/* Villa select for Other Income - Optional */}
                      {formData.type === 'payment' && formData.category === 'other_income' && (
                        <div className="mb-3">
                          <label htmlFor="unit_id" className="form-label">
                            {unitSingular} <span className="text-muted">(Optional)</span>
                          </label>
                          <select
                            className="form-select"
                            id="unit_id"
                            name="unit_id"
                            value={formData.unit_id}
                            onChange={handleInputChange}
                          >
                            <option value="">Select {unitSingular} (Optional)</option>
                            {units && units.length > 0 ? (
                              [...units]
                                .filter(unit => unit.status === 'occupied' && unit.owner_id)
                                .sort((a, b) => {
                                  // First sort by unit_type
                                  if (a.unit_type && b.unit_type) {
                                    if (a.unit_type < b.unit_type) return -1;
                                    if (a.unit_type > b.unit_type) return 1;
                                  } else if (a.unit_type) return -1;
                                  else if (b.unit_type) return 1;

                                  // Then sort by villa_number
                                  return a.villa_number.localeCompare(b.villa_number, undefined, { numeric: true });
                                })
                                .map(unit => (
                                  <option key={unit.id} value={unit.id}>
                                    {unit.unit_type ? `${unit.unit_type} ` : ''}{unit.villa_number}{unit.owner_name ? ` (${unit.owner_name})` : ''}
                                  </option>
                                ))
                            ) : (
                              <option disabled>No units available</option>
                            )}
                          </select>
                          {formData.unit_id && (() => {
                            const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
                            if (selectedUnit) {
                              return (
                                <div className="mt-2 p-2 bg-light rounded border">
                                  <small className="text-muted">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Selected: <strong>{selectedUnit.unit_type ? `${selectedUnit.unit_type} ` : ''}{selectedUnit.villa_number}</strong>
                                    {selectedUnit.owner_name && <span> - {selectedUnit.owner_name}</span>}
                                  </small>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}

                      {(formData.type === 'payment' || formData.type === 'expense') && (formData.category === 'maintenance' || formData.category === 'booking') && (
                        <div className="mb-3">
                          {(!isEditMode || (isEditMode && (formData.type !== 'payment' || (formData.category !== 'maintenance' || formData.category !== 'booking')))) && (
                            <>
                              <label htmlFor="unit_id" className="form-label">{unitSingular}</label>
                              <select
                                className="form-select mb-3"
                                id="unit_id"
                                name="unit_id"
                                value={formData.unit_id}
                                onChange={handleInputChange}
                                required={formData.type === 'payment'}
                              >
                                <option value="">Select {unitSingular}</option>
                                {units && units.length > 0 ? (
                                  // Sort units first by type, then by number - only show occupied units with owners
                                  [...units]
                                    .filter(unit => unit.status === 'occupied' && unit.owner_id) // Only show occupied units with assigned owners
                                    .sort((a, b) => {
                                      // First sort by unit_type
                                      if (a.unit_type && b.unit_type) {
                                        if (a.unit_type < b.unit_type) return -1;
                                        if (a.unit_type > b.unit_type) return 1;
                                      } else if (a.unit_type) return -1;
                                      else if (b.unit_type) return 1;

                                      // Then sort by villa_number
                                      return a.villa_number.localeCompare(b.villa_number, undefined, { numeric: true });
                                    })
                                    .map(unit => (
                                      <option key={unit.id} value={unit.id}>
                                        {unit.unit_type ? `${unit.unit_type} ` : ''}{unit.villa_number}{unit.owner_name ? ` (${unit.owner_name})` : ''}
                                      </option>
                                    ))
                                ) : (
                                  <option disabled>No occupied units with owners available</option>
                                )}
                              </select>
                              {units && units.filter(unit => unit.status === 'occupied' && unit.owner_id).length === 0 && (
                                <div className="form-text text-warning">
                                  <small>No occupied units with assigned owners found. Only occupied units with owners are shown for payments.</small>
                                </div>
                              )}
                            </>
                          )}

                          {/* Display maintenance fee and unit info when unit is selected */}
                          {formData.unit_id && (() => {
                            const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
                            if (selectedUnit) {
                              const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
                              const ownerName = selectedUnit.owner_name || 'N/A';

                              return (
                                <div className="p-3 bg-light rounded border">
                                  <h6 className="mb-2 text-primary">
                                    <i className="fas fa-info-circle me-2"></i>
                                    Unit Information
                                  </h6>
                                  <div className="row">
                                    <div className="col-md-6">
                                      <small className="text-muted">Owner</small>
                                      <div className="fw-bold">{ownerName}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <small className="text-muted">Monthly Maintenance Fee</small>
                                      <div className="fw-bold text-success">
                                        {maintenanceFee > 0 ? `₹${parseFloat(maintenanceFee).toLocaleString('en-IN')}` : 'Not set'}
                                      </div>
                                    </div>
                                  </div>
                                  {maintenanceFee > 0 && (
                                    <div className="mt-2">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                          <i className="fas fa-lightbulb me-1"></i>
                                          <strong>Quick Action:</strong> Use maintenance fee as transaction amount
                                        </small>
                                        {!isEditMode && (formData.status !== 'pending') && formData.category === 'maintenance' && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => {
                                              setFormData(prev => ({
                                                ...prev,
                                                amount: maintenanceFee.toString(),
                                                category: 'maintenance'
                                              }));
                                            }}
                                          >
                                            <i className="fas fa-hand-holding-usd me-1"></i>
                                            Set ₹{parseFloat(maintenanceFee).toLocaleString('en-IN')}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}

                      {/* advance amount Section - Only show for payments and when unit is selected */}
                      {(
                        (!isEditMode && (formData.category === 'maintenance' || formData.category === 'booking') && formData.type === 'payment' && formData.unit_id && unitAdvancedBalance > 0) ||
                        (isEditMode && formData.type === 'payment' && formData.unit_id && unitAdvancedBalance > 0)
                      ) && (
                          <div className="mb-3">
                            <div className="card border-info">
                              <div className="card-header bg-info text-white">
                                <h6 className="mb-0">
                                  <i className="fas fa-hand-holding-usd me-2"></i>
                                  Advance Amount Available: ₹{unitAdvancedBalance.toFixed(2)}
                                </h6>
                              </div>
                              {!formData.amount && (
                                <div className="alert alert-info mb-0 rounded-0">
                                  <i className="fas fa-info-circle me-2"></i>
                                  <strong>Tip:</strong> You can use advance amount independently.
                                </div>
                              )}
                              <div className="card-body">
                                <div className="form-check mb-3">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="useAdvancedAmount"
                                    checked={useAdvancedAmount}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setUseAdvancedAmount(isChecked);

                                      // Auto-fill amount field when transaction type is payment, category is booking, and unit is selected
                                      if (formData.type === 'payment' && formData.category === 'booking' && formData.unit_id) {
                                        if (isChecked && unitAdvancedBalance > 0) {
                                          // If advanced amount is used, set amount to advanced balance
                                          setFormData(prev => ({ ...prev, amount: unitAdvancedBalance.toString() }));
                                        } else {
                                          // If advanced amount is not used, leave amount field blank
                                          setFormData(prev => ({ ...prev, amount: '' }));
                                        }
                                      }

                                      if (!isChecked) {
                                        setAdvancedAmountToUse(null);
                                        setAdvancedAmountError(null);

                                        // For partially_paid status, check if we should restore original values
                                        if (formData.status === 'partially_paid' && originalPartiallyPaidValues.partial_paid_amount !== '') {
                                          // Restore original values when unchecking advance amount for partially_paid
                                          setFormData(prev => ({
                                            ...prev,
                                            partial_paid_amount: originalPartiallyPaidValues.partial_paid_amount,
                                            remaining_amount: originalPartiallyPaidValues.remaining_amount,
                                            status: 'partially_paid' // Reset status back to partially_paid
                                          }));
                                        } else if (formData.status === 'paid') {
                                          // For paid status, set partial_paid_amount to full amount when unchecking advance
                                          const totalAmount = parseFloat(formData.amount) || 0;
                                          setFormData(prev => ({
                                            ...prev,
                                            partial_paid_amount: totalAmount.toFixed(2),
                                            remaining_amount: '0.00'
                                          }));
                                        } else {
                                          // Recalculate partial_paid_amount and remaining_amount when unchecking advance
                                          if (formData.status === 'partially_paid' || formData.status === 'paid') {
                                            const totalAmount = parseFloat(formData.amount) || 0;
                                            const partialAmount = parseFloat(formData.partial_paid_amount) || 0;
                                            const remainingAmount = Math.max(0, totalAmount - partialAmount);

                                            setFormData(prev => ({
                                              ...prev,
                                              remaining_amount: remainingAmount.toFixed(2)
                                            }));
                                          }
                                        }

                                        // Payment method will be auto-updated by recalculatePaymentBreakdown
                                      } else {
                                        // Auto-select maximum usable amount
                                        const transactionAmount = parseFloat(formData.amount) || 0;
                                        const remainingAmount = parseFloat(formData.remaining_amount) || 0;

                                        let maxUsable = unitAdvancedBalance;
                                        if (transactionAmount > 0) {
                                          if (formData.status === 'partially_paid' && remainingAmount > 0) {
                                            // For partially_paid status with remaining amount, limit to remaining amount
                                            maxUsable = Math.min(unitAdvancedBalance, remainingAmount);
                                          } else if (formData.status === 'paid' && remainingAmount > 0) {
                                            // For paid status with remaining amount, limit to remaining amount
                                            maxUsable = Math.min(unitAdvancedBalance, remainingAmount);
                                          } else if (formData.status === 'paid' && remainingAmount === 0) {
                                            // For paid status with no remaining amount, use the original remaining amount from stored values
                                            if (originalPartiallyPaidValues.remaining_amount !== '') {
                                              const originalRemaining = parseFloat(originalPartiallyPaidValues.remaining_amount) || 0;
                                              maxUsable = Math.min(unitAdvancedBalance, originalRemaining);
                                            } else {
                                              maxUsable = Math.min(unitAdvancedBalance, transactionAmount);
                                            }
                                          } else {
                                            maxUsable = Math.min(unitAdvancedBalance, transactionAmount);
                                          }
                                        }

                                        setAdvancedAmountToUse(maxUsable);

                                        // For partially_paid status, check if we should restore original values
                                        if (formData.status === 'partially_paid' && originalPartiallyPaidValues.partial_paid_amount !== '') {
                                          // Restore original values when checking advance amount for partially_paid
                                          setFormData(prev => ({
                                            ...prev,
                                            partial_paid_amount: originalPartiallyPaidValues.partial_paid_amount,
                                            remaining_amount: originalPartiallyPaidValues.remaining_amount
                                          }));
                                        } else if (formData.status === 'paid') {
                                          // For paid status, calculate cash payment when checking advance
                                          const totalAmount = parseFloat(formData.amount) || 0;
                                          const cashNeeded = Math.max(0, totalAmount - maxUsable);
                                          setFormData(prev => ({
                                            ...prev,
                                            partial_paid_amount: cashNeeded.toFixed(2),
                                            remaining_amount: '0.00'
                                          }));
                                        } else {
                                          // Recalculate partial_paid_amount and remaining_amount when checking advance
                                          if (formData.status === 'partially_paid' || formData.status === 'paid') {
                                            const totalAmount = parseFloat(formData.amount) || 0;
                                            const cashNeeded = Math.max(0, totalAmount - maxUsable);
                                            const totalPaid = cashNeeded + maxUsable;
                                            const newRemainingAmount = Math.max(0, totalAmount - totalPaid);

                                            setFormData(prev => ({
                                              ...prev,
                                              partial_paid_amount: cashNeeded.toFixed(2),
                                              remaining_amount: newRemainingAmount.toFixed(2)
                                            }));

                                            // Note: Payment status will be changed manually by user selection
                                            // No automatic status change when using advance amount
                                          }
                                        }

                                        // Payment method will be auto-updated by recalculatePaymentBreakdown
                                      }
                                    }}
                                  />
                                  <label className="form-check-label fw-bold" htmlFor="useAdvancedAmount">
                                    Use advance amount
                                  </label>
                                </div>

                                <input
                                  type='hidden'
                                  className='form-control'
                                  name='already_used_advance_amount'
                                  id='already_used_advance_amount'
                                  value={advancedAmountToUse}
                                />

                                {useAdvancedAmount && (
                                  <div>
                                    <div className="mb-3">
                                      <label className="form-label">Amount to Use from Advance Balance</label>
                                      <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                          type="text"
                                          className={`form-control ${formData.category === 'booking' && advancedAmountError ? 'is-invalid' : ''}`}
                                          value={advancedAmountToUse}
                                          onChange={(e) => {
                                            // Only clean non-numeric characters, don't validate yet
                                            const cleanedValue = e.target.value.replace(/[^0-9.]/g, '');
                                            const modifiedEvent = {...e, target: {...e.target, value: cleanedValue}};
                                            handleAdvancedAmountChange(modifiedEvent);
                                          }}
                                          onBlur={validateAdvancedAmount}
                                          placeholder="0.00"
                                        />
                                      </div>
                                      {formData.category === 'booking' && advancedAmountError && (
                                        <div className="invalid-feedback d-block">
                                          <i className="fas fa-exclamation-circle me-1"></i>
                                          {advancedAmountError}
                                        </div>
                                      )}
                                      <div className="form-text">
                                        {(() => {
                                          const transactionAmount = parseFloat(formData.amount) || 0;
                                          const remainingAmount = parseFloat(formData.remaining_amount) || 0;

                                          let maxUsable = unitAdvancedBalance;
                                          if (formData.status === 'partially_paid' && remainingAmount > 0) {
                                            // For partially_paid status with remaining amount, limit to remaining amount
                                            maxUsable = Math.min(unitAdvancedBalance, remainingAmount);
                                          } else if (formData.status === 'paid' && remainingAmount > 0) {
                                            // For paid status with remaining amount, limit to remaining amount
                                            maxUsable = Math.min(unitAdvancedBalance, remainingAmount);
                                          } else if (formData.status === 'paid' && remainingAmount === 0) {
                                            // For paid status with no remaining amount, use the original remaining amount from stored values
                                            if (originalPartiallyPaidValues.remaining_amount !== '') {
                                              const originalRemaining = parseFloat(originalPartiallyPaidValues.remaining_amount) || 0;
                                              maxUsable = Math.min(unitAdvancedBalance, originalRemaining);
                                            } else {
                                              maxUsable = transactionAmount > 0
                                                ? Math.min(unitAdvancedBalance, transactionAmount)
                                                : unitAdvancedBalance;
                                            }
                                          } else {
                                            // For other statuses, limit to transaction amount
                                            maxUsable = transactionAmount > 0
                                              ? Math.min(unitAdvancedBalance, transactionAmount)
                                              : unitAdvancedBalance;
                                          }
                                          
                                          if ((formData.status === 'partially_paid' || formData.status === 'paid') && formData.remaining_amount) {
                                            return `Maximum usable: ₹${maxUsable.toFixed(2)} (limited to remaining amount to be paid)`;
                                          } else {
                                            return transactionAmount > 0
                                              ? `Maximum usable: ₹${maxUsable.toFixed(2)}`
                                              : `Available balance: ₹${unitAdvancedBalance.toFixed(2)} (Enter transaction amount first for exact limit)`;
                                          }
                                        })()}
                                      </div>
                                    </div>

                                    {/* Payment Breakdown */}
                                    <div className="alert alert-light border d-block">
                                      <h6 className="alert-heading">Payment Breakdown</h6>
                                      <div className="payment-breakdown-card-outer d-flex align-items-center">
                                        <div className="payment-breakdown-cards col-lg-4 col-md-12">
                                          <small className="text-muted">
                                            {formData.status === 'partially_paid' ? 'Total Due' : 'Total Amount'}
                                          </small>
                                          <div className="fw-bold">₹{(parseFloat(formData.amount) || 0).toFixed(2)}</div>
                                        </div>
                                        <div className="payment-breakdown-cards col-lg-4 col-md-12">
                                          <small className="text-muted">Advance Used</small>
                                          <div className="fw-bold text-success">₹{paymentBreakdown.advancedUsed.toFixed(2)}</div>
                                        </div>
                                        {paymentBreakdown.cashPayment > 0 && (
                                          <div className="payment-breakdown-cards col-lg-4 col-md-12">
                                            <small className="text-muted">Cash Payment</small>
                                            <div className="fw-bold text-primary">₹{paymentBreakdown.cashPayment.toFixed(2)}</div>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Show Already Paid for both partially_paid and paid status when there's original partially paid amount */}
                                      {originalPartiallyPaidValues.partial_paid_amount !== '' && (
                                        <div className="payment-breakdown-card-outer d-flex align-items-center mt-2 pt-2 border-top">
                                          <div className="payment-breakdown-cards col-lg-6 col-md-12">
                                            <small className="text-muted">Already Paid</small>
                                            <div className="fw-bold text-info">
                                              ₹{parseFloat(originalPartiallyPaidValues.partial_paid_amount).toFixed(2)}
                                            </div>
                                          </div>
                                          <div className="payment-breakdown-cards col-lg-6 col-md-12">
                                            <small className="text-muted">Remaining</small>
                                            <div className="fw-bold text-warning">
                                              ₹{parseFloat(originalPartiallyPaidValues.remaining_amount).toFixed(2)}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {/* Show Total Paid and Remaining only when there's no original partially paid amount */}
                                      {formData.status === 'partially_paid' && formData.remaining_amount && originalPartiallyPaidValues.partial_paid_amount === '' && (
                                        <div className="payment-breakdown-card-outer d-flex align-items-center mt-2 pt-2 border-top">
                                          <div className="payment-breakdown-cards col-lg-6 col-md-12">
                                            <small className="text-muted">Total Paid</small>
                                            <div className="fw-bold text-success">
                                              ₹{(paymentBreakdown.advancedUsed + paymentBreakdown.cashPayment).toFixed(2)}
                                            </div>
                                          </div>
                                          <div className="payment-breakdown-cards col-lg-6 col-md-12">
                                            <small className="text-muted">Remaining</small>
                                            <div className="fw-bold text-warning">
                                              ₹{parseFloat(formData.remaining_amount).toFixed(2)}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Conditional rendering for partial_paid_amount field */}
                      {(() => {
                        if (formData.partial_paid_amount > 0 && formData.status !== 'pending' && formData.type === 'payment' && formData.category === 'maintenance') {

                          // Determine if the field should be editable based on status and advance amount usage
                          const isPartiallyPaid = formData.status === 'partially_paid';
                          const isPaid = formData.status === 'paid';
                          const isEditable = (!isPartiallyPaid && !isPaid) || ((isPartiallyPaid || isPaid) && !useAdvancedAmount);

                          return (
                            <div className="mb-3">
                              <label htmlFor="partial_paid_amount" className="form-label">Enter Amount to Pay</label>
                              <div className="input-group">
                                <span className="input-group-text">₹</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  id="partial_paid_amount"
                                  name="partial_paid_amount"
                                  value={formData.partial_paid_amount}
                                  onChange={handleInputChange}
                                  placeholder="Enter amount to pay"
                                  required={formData.status === 'partially_paid'}
                                  disabled={!isEditable}
                                  onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                                  }}
                                />
                                <input type='hidden'
                                  className='form-control'
                                  id='already_partial_paid_amount'
                                  name='already_partial_paid_amount'
                                  value={formData.already_partial_paid_amount} />
                              </div>
                              {isPartiallyPaid && useAdvancedAmount && (
                                <div className="form-text text-muted">
                                  <i className="fas fa-info-circle me-1"></i>
                                  Field is read-only when using advance amount for partially paid transactions
                                </div>
                              )}
                            </div>
                          );
                        }
                      })()}

                      {/* Conditional rendering for remaining_amount field */}
                      {(() => {
                        const remainingAmount = parseFloat(formData.remaining_amount) || 0;
                        const shouldShowRemainingField = (formData.status === 'partially_paid' && formData.type === 'payment' && formData.category === 'maintenance');

                        if (!shouldShowRemainingField) {
                          return null;
                        }

                        return (
                          <div className="mb-3">
                            <label htmlFor="remaining_amount" className="form-label">Remaining Amount</label>
                            <div className="input-group">
                              <span className="input-group-text">₹</span>
                              <input
                                type="text"
                                className="form-control"
                                id="remaining_amount"
                                name="remaining_amount"
                                value={formData.remaining_amount}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                placeholder="Enter remaining amount to be paid"
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                                }}
                                disabled
                              />
                            </div>
                          </div>
                        );
                      })()}

                      <div className="mb-3">
                        <label htmlFor="amount" className="form-label">Amount</label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="text"
                            className={`form-control ${!validateMaintenanceAmount().isValid ? 'is-invalid' : ''} ${formData.category === 'maintenance' && formData.status === 'partially_paid' ? 'bg-light' : ''
                              }`}
                            id="amount"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            max={(() => {
                              if (formData.category === 'maintenance' && formData.unit_id && formData.status !== 'partially_paid') {
                                const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
                                if (selectedUnit) {
                                  const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
                                  return maintenanceFee > 0 ? maintenanceFee : undefined;
                                }
                              }
                              return undefined;
                            })()}
                            disabled={formData.category === 'maintenance' && formData.type === 'payment' && formData.status !== ''}
                            required
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                            }}
                          />
                        </div>

                        {!validateMaintenanceAmount().isValid && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {validateMaintenanceAmount().errorMessage}
                          </div>
                        )}
                        {formData.type === 'payment' && formData.category === 'maintenance' && formData.unit_id && (() => {
                          const selectedUnit = units.find(u => u.id.toString() === formData.unit_id);
                          if (selectedUnit) {
                            const maintenanceFee = selectedUnit.maintenance_fee || settings?.maintenance_fee || 0;
                            if (maintenanceFee > 0) {
                              if (formData.status === 'partially_paid') {
                                return (
                                  <div className="form-text text-muted">
                                    <i className="fas fa-lock me-1"></i>
                                    Amount is fixed at maintenance fee (₹{maintenanceFee.toLocaleString('en-IN')})
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="form-text text-info">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Maximum allowed: ₹{maintenanceFee.toLocaleString('en-IN')}
                                  </div>
                                );
                              }
                            }
                          }
                          return null;
                        })()}
                      </div>

                      {/* File upload field - show for expense, income transactions and booking category with villa selected */}
                      {(formData.type === 'expense' || formData.type === 'payment' || (formData.category === 'booking' && formData.unit_id)) && (
                        <div className="mb-3">
                          <label htmlFor="document" className="form-label">
                            <i className="fas fa-file-upload me-2"></i>
                            {formData.type === 'expense' ? 'Expense Documents' : formData.type === 'payment' ? 'Income Documents' : 'Booking Documents'} (Optional)
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            id="document"
                            name="document"
                            onChange={handleFileChange}
                            accept=".jpg,.jpeg,.png,.pdf"
                            multiple
                          />
                          <div className="form-text">
                            <i className="fas fa-info-circle me-1"></i>
                            Upload receipts, invoices, or supporting documents. You can select multiple files. Accepted formats: JPG, JPEG, PNG, PDF (Max 10MB each)
                          </div>
                          
                          {/* Display selected files */}
                          {selectedFiles.length > 0 && (
                            <div className="mt-2">
                              <h6 className="text-muted mb-2">Selected Files:</h6>
                              {selectedFiles.map((file, index) => (
                                <div key={index} className="alert alert-info py-2 mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <i className="fas fa-check-circle me-2"></i>
                                      <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleRemoveDocument(index)}
                                      title="Remove this file"
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleRemoveDocument()}
                                title="Remove all selected files"
                              >
                                <i className="fas fa-trash-alt me-1"></i>
                                Remove All Files
                              </button>
                            </div>
                          )}
                          
                          {/* Display existing documents */}
                          {(() => {
                            console.log('=== EDIT MODAL DISPLAY DEBUG ===');
                            console.log('formData.document_url:', formData.document_url);
                            console.log('formData.document_url type:', typeof formData.document_url);
                            console.log('formData.document_url is array:', Array.isArray(formData.document_url));
                            
                            // Handle both single URL and array formats dynamically
                            let documentUrls = [];
                            
                            if (formData.document_url) {
                              if (Array.isArray(formData.document_url)) {
                                // Already an array
                                documentUrls = formData.document_url;
                              } else if (typeof formData.document_url === 'string') {
                                // Check if it's a JSON string
                                try {
                                  const parsed = JSON.parse(formData.document_url);
                                  if (Array.isArray(parsed)) {
                                    documentUrls = parsed;
                                  } else {
                                    // Single URL
                                    documentUrls = [formData.document_url];
                                  }
                                } catch (e) {
                                  // Not JSON, treat as single URL
                                  documentUrls = [formData.document_url];
                                }
                              }
                            }
                            
                            console.log('Processed documentUrls for display:', documentUrls);
                            console.log('Document URLs length:', documentUrls.length);
                            console.log('=== END EDIT MODAL DISPLAY DEBUG ===');
                            
                            return documentUrls.length > 0;
                          })() && (
                            <div className="mt-2">
                              <h6 className="text-muted mb-2">Current Documents:</h6>
                              {(() => {
                                // Handle both single URL and array formats dynamically
                                let documentUrls = [];
                                
                                if (formData.document_url) {
                                  if (Array.isArray(formData.document_url)) {
                                    // Already an array
                                    documentUrls = formData.document_url;
                                  } else if (typeof formData.document_url === 'string') {
                                    // Check if it's a JSON string
                                    try {
                                      const parsed = JSON.parse(formData.document_url);
                                      if (Array.isArray(parsed)) {
                                        documentUrls = parsed;
                                      } else {
                                        // Single URL
                                        documentUrls = [formData.document_url];
                                      }
                                    } catch (e) {
                                      // Not JSON, treat as single URL
                                      documentUrls = [formData.document_url];
                                    }
                                  }
                                }
                                
                                return documentUrls.map((url, index) => (
                                  <div key={index} className="alert alert-success py-2 mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <i className="fas fa-file-alt me-2"></i>
                                        <strong>Document {index + 1}:</strong>
                                        <a
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="ms-2 text-decoration-none"
                                        >
                                          View Document
                                          <i className="fas fa-external-link-alt ms-1"></i>
                                        </a>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => {
                                          const updatedUrls = documentUrls.filter((_, i) => i !== index);
                                          setFormData(prev => ({ ...prev, document_url: updatedUrls }));
                                        }}
                                        title="Remove this document"
                                      >
                                        <i className="fas fa-trash-alt"></i>
                                      </button>
                                    </div>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      )}

                    </div>

                    <div className="col-md-6">

                      <div className={`${isEditMode ? 'd-none' : ''} mb-3`}>
                        <label htmlFor="payformonth" className="form-label">Paid Month</label>
                        <select
                          className="form-select"
                          id="month_id"
                          name="month_id"
                          value={formData.month_id}
                          onChange={handleInputChange}
                          disabled={isEditMode}
                        >
                          <option value="">Select Month</option>
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>
                      <div className={`${isEditMode ? 'd-none' : ''} mb-3`}>
                        <label htmlFor="year" className="form-label">Year</label>
                        <select
                          className="form-select"
                          id="year"
                          name="year"
                          value={formData.year}
                          onChange={handleInputChange}
                          disabled={isEditMode}
                        >
                          {Array.from({ length: 6 }, (_, i) => {
                            const year = new Date().getFullYear() - 5 + i;
                            return (
                              <option key={year} value={year.toString()}>
                                {year}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {isEditMode && (
                        <>
                          <div className="mb-3">
                            <label htmlFor="payformonth" className="form-label">Paid For</label>
                            <input
                              type="text"
                              className="form-control"
                              value={`${getMonthName(formData.month_id) + ', ' + formData.year}`}
                              disabled
                            />
                          </div>
                        </>
                      )}

                      {formData.type === 'payment' && (
                        <div className="mb-3">
                          <label htmlFor="payment_method" className="form-label">Payment Method</label>
                          <select
                            className="form-select bg-light"
                            id="payment_method"
                            name="payment_method"
                            value={formData.payment_method}
                          >
                            <option value="cash_payment">Cash Payment</option>
                            <option value="from_advance_balance">From Advance Balance</option>
                            <option value="cash_from_advance_balance">Cash + From Advance Balance</option>
                          </select>
                        </div>
                      )}

                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">Payment Status</label>
                        <select
                          className="form-select"
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          required
                        >
                          {formData.category === 'booking' ? (
                            // Only show "Paid" option if category is booking
                            <option value="paid">Paid</option>
                          ) : (
                            // Otherwise show all options
                            <>
                              <option value="pending">Pending</option>
                              <option value="paid">Paid</option>
                              <option value="hold">Hold</option>
                              <option value="partially_paid">Partially Paid</option>
                            </>
                          )}
                        </select>
                      </div>
                      <input
                        type="hidden"
                        className="form-control"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                      />
                      {(!isEditMode && (formData.status === 'paid' || formData.status === 'pending')) ||
                        (isEditMode && formData.status === 'paid') ? (
                        <div className="mb-3">
                          <label htmlFor="date" className="form-label">Payment Date</label>
                          <input
                            type="date"
                            className="form-control"
                            id="payment_date"
                            name="payment_date"
                            value={formData.payment_date}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      ) : ''}

                      {formData.status === 'partially_paid' && (
                        <div className="mb-3">
                          <label htmlFor="date" className="form-label">Partially Payment Date</label>
                          <input
                            type="date"
                            className="form-control"
                            id="partially_payment_date"
                            name="partially_payment_date"
                            value={formData.partially_payment_date}
                            onChange={handleInputChange}
                            disabled={isPartiallyPaymentDateFromDB}
                            required
                          />
                        </div>
                      )}

                      <div className="mb-3">
                        <label htmlFor="remarks" className="form-label">Remarks</label>
                        <textarea
                          className="form-control"
                          id="remarks"
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleInputChange}
                          rows="4"
                          placeholder="Enter any additional notes or comments..."
                        ></textarea>
                      </div>

                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setIsEditMode(false);
                    setCurrentTransactionId(null);
                    setFormData({
                      type: 'payment',
                      amount: '',
                      category: '',
                      unit_id: '',
                      month_id: '',
                      date: new Date().toISOString().split('T')[0],
                      payment_date: new Date().toISOString().split('T')[0],
                      partially_payment_date: '',
                      partial_paid_amount: '',
                      remarks: '',
                      status: 'pending',
                      remaining_amount: '',
                      year: new Date().getFullYear().toString(),
                      payment_method: 'cash_payment',
                      document_url: []
                    });
                    // Reset advance amount states
                    setUseAdvancedAmount(false);
                    setAdvancedAmountToUse(null);
                    setUnitAdvancedBalance(0);
                    setPaymentBreakdown({ total: 0, advancedUsed: 0, cashPayment: 0 });
                    // Reset the flag for partially_payment_date
                    setIsPartiallyPaymentDateFromDB(false);
                    // Reset file upload state
                    setSelectedFiles([]);
                  }}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="transactionForm"
                  className="btn btn-primary"
                  disabled={loading || !validateMaintenanceAmount().isValid}
                >
                  <i className={`fas fa-${isEditMode ? 'save' : 'plus-circle'} me-2`}></i>
                  {isEditMode ? 'Update Transaction' : 'Add Transaction'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* advance amount Form Modal */}
      {showAdvancedForm && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className={`modal-header ${themeClassName}`}>
                <h5 className="modal-title">
                  <i className="fas fa-hand-holding-usd me-2"></i>
                  {isEditAdvancedMode ? 'Edit advance amount' : 'Add advance amount'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAdvancedForm(false);
                    setIsEditAdvancedMode(false);
                    setCurrentAdvancedId(null);
                    setAdvancedFormData({
                      unit_id: '',
                      amount: '',
                      payment_date: new Date().toISOString().split('T')[0],
                      remarks: '',
                      advance_type: 'credit',
                      document_url: []
                    });
                    setSelectedAdvancedFiles([]);
                  }}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Advance amount:</strong> Record credit or debit amounts for residents&apos; accounts.
                </div>
                <form onSubmit={handleAdvancedSubmit} id="advancedForm">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="advance_unit_id" className="form-label">{unitSingular} <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          id="advance_unit_id"
                          name="unit_id"
                          value={advancedFormData.unit_id}
                          onChange={handleAdvancedInputChange}
                          required
                        >
                          <option value="">Select {unitSingular}</option>
                          {units && units.length > 0 ? (
                            [...units]
                              .filter(unit => unit.status === 'occupied' && unit.owner_id) // Only show occupied units with assigned owners
                              .sort((a, b) => {
                                // First sort by unit_type
                                if (a.unit_type && b.unit_type) {
                                  if (a.unit_type < b.unit_type) return -1;
                                  if (a.unit_type > b.unit_type) return 1;
                                } else if (a.unit_type) return -1;
                                else if (b.unit_type) return 1;

                                // Then sort by villa_number
                                return a.villa_number.localeCompare(b.villa_number, undefined, { numeric: true });
                              })
                              .map(unit => (
                                <option key={unit.id} value={unit.id}>
                                  {unit.unit_type ? `${unit.unit_type} ` : ''}{unit.villa_number}{unit.owner_name ? ` (${unit.owner_name})` : ''}
                                </option>
                              ))
                          ) : (
                            <option disabled>No occupied units with owners available</option>
                          )}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="advance_amount" className="form-label">Advance Amount <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="text"
                            className="form-control"
                            id="advance_amount"
                            name="amount"
                            value={advancedFormData.amount}
                            onChange={handleAdvancedInputChange}
                            min="0"
                            step="0.01"
                            placeholder="Enter advance amount"
                            required
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                            }}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="advance_type" className="form-label">Advance Type <span className="text-danger">*</span></label>
                        <select
                          className="form-select"
                          id="advance_type"
                          name="advance_type"
                          value={advancedFormData.advance_type}
                          onChange={handleAdvancedInputChange}
                          required
                        >
                          <option value="credit">Credit</option>
                          <option value="debit">Debit</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="payment_date" className="form-label">Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          id="payment_date"
                          name="payment_date"
                          value={advancedFormData.payment_date}
                          onChange={handleAdvancedInputChange}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="advance_remarks" className="form-label">Remarks</label>
                        <textarea
                          className="form-control"
                          id="advance_remarks"
                          name="remarks"
                          value={advancedFormData.remarks}
                          onChange={handleAdvancedInputChange}
                          rows="4"
                          placeholder="Enter any additional notes about this advance payment..."
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  {/* File upload field for advance amount documents */}
                  <div className="row">
                    <div className="col-12">
                      <div className="mb-3">
                        <label htmlFor="advanced_document" className="form-label">
                          <i className="fas fa-file-upload me-2"></i>
                          Documents (Optional)
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          id="advanced_document"
                          name="advanced_document"
                          onChange={handleAdvancedFileChange}
                          accept=".jpg,.jpeg,.png,.pdf"
                          multiple
                        />
                        <div className="form-text">
                          <i className="fas fa-info-circle me-1"></i>
                          Upload receipts, invoices, or supporting documents. You can select multiple files. Accepted formats: JPG, JPEG, PNG, PDF (Max 10MB each)
                        </div>
                        
                        {/* Display selected files */}
                        {selectedAdvancedFiles.length > 0 && (
                          <div className="mt-2">
                            <h6 className="text-muted mb-2">Selected Files:</h6>
                            {selectedAdvancedFiles.map((file, index) => (
                              <div key={index} className="alert alert-info py-2 mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <i className="fas fa-check-circle me-2"></i>
                                    <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleRemoveAdvancedDocument(index)}
                                    title="Remove this file"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleRemoveAdvancedDocument()}
                              title="Remove all selected files"
                            >
                              <i className="fas fa-trash-alt me-1"></i>
                              Remove All Files
                            </button>
                          </div>
                        )}
                        
                        {/* Display existing documents */}
                        {(() => {
                          // Handle both single URL and array formats dynamically
                          let documentUrls = [];
                          
                          if (advancedFormData.document_url) {
                            if (Array.isArray(advancedFormData.document_url)) {
                              // Already an array
                              documentUrls = advancedFormData.document_url;
                            } else if (typeof advancedFormData.document_url === 'string') {
                              // Check if it's a JSON string
                              try {
                                const parsed = JSON.parse(advancedFormData.document_url);
                                if (Array.isArray(parsed)) {
                                  documentUrls = parsed;
                                } else {
                                  // Single URL
                                  documentUrls = [advancedFormData.document_url];
                                }
                              } catch (e) {
                                // Not JSON, treat as single URL
                                documentUrls = [advancedFormData.document_url];
                              }
                            }
                          }
                          
                          return documentUrls.length > 0;
                        })() && (
                          <div className="mt-2">
                            <h6 className="text-muted mb-2">Current Documents:</h6>
                            {(() => {
                              // Handle both single URL and array formats dynamically
                              let documentUrls = [];
                              
                              if (advancedFormData.document_url) {
                                if (Array.isArray(advancedFormData.document_url)) {
                                  // Already an array
                                  documentUrls = advancedFormData.document_url;
                                } else if (typeof advancedFormData.document_url === 'string') {
                                  // Check if it's a JSON string
                                  try {
                                    const parsed = JSON.parse(advancedFormData.document_url);
                                    if (Array.isArray(parsed)) {
                                      documentUrls = parsed;
                                    } else {
                                      // Single URL
                                      documentUrls = [advancedFormData.document_url];
                                    }
                                  } catch (e) {
                                    // Not JSON, treat as single URL
                                    documentUrls = [advancedFormData.document_url];
                                  }
                                }
                              }
                              
                              return documentUrls.map((url, index) => (
                                <div key={index} className="alert alert-success py-2 mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <i className="fas fa-file-alt me-2"></i>
                                      <strong>Document {index + 1}:</strong>
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ms-2 text-decoration-none"
                                      >
                                        View Document
                                        <i className="fas fa-external-link-alt ms-1"></i>
                                      </a>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => {
                                        const updatedUrls = documentUrls.filter((_, i) => i !== index);
                                        setAdvancedFormData(prev => ({ ...prev, document_url: updatedUrls }));
                                      }}
                                      title="Remove this document"
                                    >
                                      <i className="fas fa-trash-alt"></i>
                                    </button>
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Note:</strong> Credit amounts will be added to the unit&apos;s account balance, while debit amounts will be deducted.
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAdvancedForm(false);
                    setIsEditAdvancedMode(false);
                    setCurrentAdvancedId(null);
                    setAdvancedFormData({
                      unit_id: '',
                      amount: '',
                      payment_date: new Date().toISOString().split('T')[0],
                      remarks: '',
                      advance_type: 'credit',
                      document_url: []
                    });
                    setSelectedAdvancedFiles([]);
                  }}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="advancedForm"
                  className="btn btn-success"
                  disabled={loading}
                >
                  <i className={`fas fa-${isEditAdvancedMode ? 'save' : 'hand-holding-usd'} me-2`}></i>
                  {isEditAdvancedMode ? 'Update advance amount' : 'Add advance amount'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showBankBalanceModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className={`modal-header ${themeClassName}`}>
                <h5 className="modal-title">
                  <i className="fas fa-hand-holding-usd me-2"></i>
                  {isEditBankBalance ? 'Edit Bank Balance' : 'Add Bank Balance'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseBankBalanceModal}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                {bankBalanceError && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {bankBalanceError}
                  </div>
                )}
                {bankBalanceSuccess && (
                  <div className="alert alert-success">
                    <i className="fas fa-check-circle me-2"></i>
                    {bankBalanceSuccess}
                  </div>
                )}
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  {isEditBankBalance
                    ? 'Bank balance records can only be updated within 30 minutes of being added.'
                    : 'Add the bank balance available for the society transactions.'
                  }
                </div>
                <form onSubmit={handleBankBalanceSubmit}>
                  <div className="d-flex flex-wrap g-3">
                    <div className="mb-3 col-md-6">
                      <label htmlFor="bank_balance_amount" className="form-label">Amount <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="text"
                          className="form-control"
                          id="bank_balance_amount"
                          name="amount"
                          value={bankBalanceFormData.amount}
                          onChange={handleBankBalanceInputChange}
                          placeholder="Enter amount"
                          required
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-3 col-md-6">
                      <label htmlFor="bank_balance_date" className="form-label">Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        id="bank_balance_date"
                        name="date"
                        value={bankBalanceFormData.date}
                        onChange={handleBankBalanceInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3 col-md-12">
                      <label htmlFor="bank_balance_description" className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        id="bank_balance_description"
                        name="description"
                        value={bankBalanceFormData.description}
                        onChange={handleBankBalanceInputChange}
                        rows="4"
                        placeholder="Enter any additional notes about this bank balance..."
                      ></textarea>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Note:</strong> This will record the bank balance for the society account.
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseBankBalanceModal}
                    >
                      <i className="fas fa-times me-2"></i>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={bankBalanceLoading}
                    >
                      {bankBalanceLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {isEditBankBalance ? 'Updating...' : 'Adding...'}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-hand-holding-usd me-2"></i>
                          {isEditBankBalance ? 'Update Bank Balance' : 'Add Bank Balance'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transactions List and Pagination - Only show for non-advanced tabs */}
      {activeTab !== 'advanced' && (
        <>
          <div className="card shadow-sm" id="transactions-section">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 financial-table">
                  <thead>
                    <tr>
                      <th>Last updated</th>
                      <th>Payment For</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Total Amount</th>
                      <th>Status</th>
                      {paginatedTransactions.some(t => t.type === 'payment') ? (
                        <>
                          <th>Paid Amount</th>
                        </>
                      ) : (
                        <></>
                      )}
                      {paginatedTransactions.some(t => t.type === 'payment') ? (
                        <>
                          <th>Remaining</th>
                          <th>{unitSingular}</th>
                        </>
                      ) : (
                        <>
                          <th>Document</th>
                          <th>Remark</th>
                        </>
                      )}
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.length > 0 ? (
                      paginatedTransactions.map(transaction => (
                        <tr key={transaction.id}>
                          <td className="text-nowrap-mobile">
                            {transaction.updated_at
                              ? formatDateIST(transaction.updated_at)
                              : formatDateToDDMMYYYY(transaction.date)}
                          </td>
                          <td>
                            {transaction.month_id && monthNames[transaction.month_id] ? (
                              <span className="badge bg-info text-white">
                                {monthNames[transaction.month_id]}{transaction.year ? `, ${transaction.year}` : ""}
                              </span>
                            ) : (
                              <span className="badge bg-info text-white">
                                {formatMonthYearFromDate(transaction.date)}
                              </span>
                            )}
                          </td>

                          <td>
                            <span className={`badge rounded-pill ${transaction.type === 'payment' ? 'bg-success' : 'bg-danger'}`}>
                              {transaction.type === 'payment' ? 'Income' : 'Expense'}
                            </span>
                          </td>
                          <td className="text-capitalize">{transaction.category ? transaction.category.replace('_', ' ') : 'N/A'}</td>
                          <td className="fw-bold">{safeFormatCurrency(transaction.amount)}</td>
                          <td>
                            <span className={`badge rounded-pill ${transaction.status === 'paid' ? 'bg-success' :
                              transaction.status === 'pending' ? 'bg-warning text-dark' :
                                transaction.status === 'hold' ? 'bg-secondary' :
                                  transaction.status === 'partially_paid' ? 'bg-info' : 'bg-light text-dark'
                              }`}>
                              {transaction.status ? transaction.status.replace('_', ' ').toUpperCase() : 'PAID'}
                            </span>
                          </td>
                          {paginatedTransactions.some(t => t.type === 'payment') ? (
                            <td>
                              {transaction.status === 'paid'
                                ? transaction.amount
                                : transaction.status === 'partially_paid'
                                  ? transaction.partially_paid_amount
                                  : '-'}
                            </td>
                          ) : (
                            <></>
                          )}
                          {paginatedTransactions.some(t => t.type === 'payment') ? (
                            transaction.type === 'payment' ? (
                              // Show remarks only for "expense" or "bank balance" categories
                              transaction.category === 'expense' || transaction.category === 'bank balance' ? (
                                <>
                                  <td className="text-muted">{transaction.remarks || '-'}</td>
                                  <td className="text-muted">
                                    {(() => {
                                      // Handle both single URL and array formats dynamically
                                      let documentUrls = [];
                                      
                                      if (transaction.document_url) {
                                        if (Array.isArray(transaction.document_url)) {
                                          // Already an array
                                          documentUrls = transaction.document_url;
                                        } else if (typeof transaction.document_url === 'string') {
                                          // Check if it's a JSON string
                                          try {
                                            const parsed = JSON.parse(transaction.document_url);
                                            if (Array.isArray(parsed)) {
                                              documentUrls = parsed;
                                            } else {
                                              // Single URL
                                              documentUrls = [transaction.document_url];
                                            }
                                          } catch (e) {
                                            // Not JSON, treat as single URL
                                            documentUrls = [transaction.document_url];
                                          }
                                        }
                                      }
                                      
                                      return (transaction.type === 'expense' || transaction.type === 'payment' || (transaction.category === 'booking' && transaction.unit_id)) && documentUrls.length > 0;
                                    })() ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {(() => {
                                          // Handle both single URL and array formats dynamically
                                          let documentUrls = [];
                                          
                                          if (transaction.document_url) {
                                            if (Array.isArray(transaction.document_url)) {
                                              // Already an array
                                              documentUrls = transaction.document_url;
                                            } else if (typeof transaction.document_url === 'string') {
                                              // Check if it's a JSON string
                                              try {
                                                const parsed = JSON.parse(transaction.document_url);
                                                if (Array.isArray(parsed)) {
                                                  documentUrls = parsed;
                                                } else {
                                                  // Single URL
                                                  documentUrls = [transaction.document_url];
                                                }
                                              } catch (e) {
                                                // Not JSON, treat as single URL
                                                documentUrls = [transaction.document_url];
                                              }
                                            }
                                          }
                                          
                                          return documentUrls.map((url, index) => (
                                            <a
                                              key={index}
                                              href={url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="btn btn-outline-primary btn-sm"
                                              title={`View Document ${index + 1}`}
                                            >
                                              <i className="fas fa-file-alt me-1"></i>
                                              Doc {index + 1}
                                            </a>
                                          ));
                                        })()}
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>
                                    {transaction.status === 'partially_paid' && transaction.remaining_amount ? (
                                      <span className="text-warning fw-bold">
                                        {safeFormatCurrency(transaction.remaining_amount)}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {transaction.unit_id ? (
                                      (() => {
                                        const unit = units.find(u => u.id === transaction.unit_id);
                                        return unit ? (
                                          <span className="text-nowrap">
                                            {unit.unit_type && (
                                              <span className="badge bg-secondary me-1">{unit.unit_type}</span>
                                            )}
                                            {unit.villa_number}
                                            {unit.owner_name && (
                                              <div className="text-muted small">{unit.owner_name}</div>
                                            )}
                                          </span>
                                        ) : (
                                          <span className="text-muted">Unit #{transaction.unit_id}</span>
                                        );
                                      })()
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                    {/* Document buttons for income transactions */}
                                      {(() => {
                                        // Handle both single URL and array formats dynamically
                                        let documentUrls = [];
                                        
                                        if (transaction.document_url) {
                                          if (Array.isArray(transaction.document_url)) {
                                            // Already an array
                                            documentUrls = transaction.document_url;
                                          } else if (typeof transaction.document_url === 'string') {
                                            // Check if it's a JSON string
                                            try {
                                              const parsed = JSON.parse(transaction.document_url);
                                              if (Array.isArray(parsed)) {
                                                documentUrls = parsed;
                                              } else {
                                                // Single URL
                                                documentUrls = [transaction.document_url];
                                              }
                                            } catch (e) {
                                              // Not JSON, treat as single URL
                                              documentUrls = [transaction.document_url];
                                            }
                                          }
                                        }
                                        
                                        return (transaction.type === 'payment') && documentUrls.length > 0;
                                      })() && (
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                          {(() => {
                                            // Handle both single URL and array formats dynamically
                                            let documentUrls = [];
                                            
                                            if (transaction.document_url) {
                                              if (Array.isArray(transaction.document_url)) {
                                                // Already an array
                                                documentUrls = transaction.document_url;
                                              } else if (typeof transaction.document_url === 'string') {
                                                // Check if it's a JSON string
                                                try {
                                                  const parsed = JSON.parse(transaction.document_url);
                                                  if (Array.isArray(parsed)) {
                                                    documentUrls = parsed;
                                                  } else {
                                                    // Single URL
                                                    documentUrls = [transaction.document_url];
                                                  }
                                                } catch (e) {
                                                  // Not JSON, treat as single URL
                                                  documentUrls = [transaction.document_url];
                                                }
                                              }
                                            }
                                            
                                            return documentUrls.map((url, index) => (
                                              <a
                                                key={index}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-outline-primary btn-sm"
                                                title={`View Document ${index + 1}`}
                                              >
                                                <i className="fas fa-file-alt me-1"></i>
                                                Doc {index + 1}
                                              </a>
                                            ));
                                          })()}
                                        </div>
                                      )}
                                  </td>
                                </>
                              )
                            ) : (
                              // For type !== payment (like "income", "transfer", etc.)
                              <>
                                <td className="text-muted">{transaction.remarks || '-'}</td>
                                <td className="text-muted">
                                  {(() => {
                                    // Handle both single URL and array formats dynamically
                                    let documentUrls = [];
                                    
                                    if (transaction.document_url) {
                                      if (Array.isArray(transaction.document_url)) {
                                        // Already an array
                                        documentUrls = transaction.document_url;
                                      } else if (typeof transaction.document_url === 'string') {
                                        // Check if it's a JSON string
                                        try {
                                          const parsed = JSON.parse(transaction.document_url);
                                          if (Array.isArray(parsed)) {
                                            documentUrls = parsed;
                                          } else {
                                            // Single URL
                                            documentUrls = [transaction.document_url];
                                          }
                                        } catch (e) {
                                          // Not JSON, treat as single URL
                                          documentUrls = [transaction.document_url];
                                        }
                                      }
                                    }
                                    
                                    return (transaction.type === 'expense' || transaction.type === 'payment' || (transaction.category === 'booking' && transaction.unit_id)) && documentUrls.length > 0;
                                  })() ? (
                                    <div className="d-flex flex-wrap gap-1">
                                      {(() => {
                                        // Handle both single URL and array formats dynamically
                                        let documentUrls = [];
                                        
                                        if (transaction.document_url) {
                                          if (Array.isArray(transaction.document_url)) {
                                            // Already an array
                                            documentUrls = transaction.document_url;
                                          } else if (typeof transaction.document_url === 'string') {
                                            // Check if it's a JSON string
                                            try {
                                              const parsed = JSON.parse(transaction.document_url);
                                              if (Array.isArray(parsed)) {
                                                documentUrls = parsed;
                                              } else {
                                                // Single URL
                                                documentUrls = [transaction.document_url];
                                              }
                                            } catch (e) {
                                              // Not JSON, treat as single URL
                                              documentUrls = [transaction.document_url];
                                            }
                                          }
                                        }
                                        
                                        return documentUrls.map((url, index) => (
                                          <a
                                            key={index}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-outline-primary btn-sm"
                                            title={`View Document ${index + 1}`}
                                          >
                                            <i className="fas fa-file-alt me-1"></i>
                                            Doc {index + 1}
                                          </a>
                                        ));
                                      })()}
                                    </div>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              </>
                            )
                          ) : (
                            <>
                              <td className="text-muted">{transaction.remarks || '-'}</td>
                              <td className="text-muted">
                                {(() => {
                                  // Handle both single URL and array formats dynamically
                                  let documentUrls = [];
                                  
                                  if (transaction.document_url) {
                                    if (Array.isArray(transaction.document_url)) {
                                      // Already an array
                                      documentUrls = transaction.document_url;
                                    } else if (typeof transaction.document_url === 'string') {
                                      // Check if it's a JSON string
                                      try {
                                        const parsed = JSON.parse(transaction.document_url);
                                        if (Array.isArray(parsed)) {
                                          documentUrls = parsed;
                                        } else {
                                          // Single URL
                                          documentUrls = [transaction.document_url];
                                        }
                                      } catch (e) {
                                        // Not JSON, treat as single URL
                                        documentUrls = [transaction.document_url];
                                      }
                                    }
                                  }
                                  
                                  return (transaction.type === 'expense' || (transaction.category === 'booking' && transaction.unit_id)) && documentUrls.length > 0;
                                })() ? (
                                  <div className="d-flex flex-wrap gap-1">
                                    {(() => {
                                      // Handle both single URL and array formats dynamically
                                      let documentUrls = [];
                                      
                                      if (transaction.document_url) {
                                        if (Array.isArray(transaction.document_url)) {
                                          // Already an array
                                          documentUrls = transaction.document_url;
                                        } else if (typeof transaction.document_url === 'string') {
                                          // Check if it's a JSON string
                                          try {
                                            const parsed = JSON.parse(transaction.document_url);
                                            if (Array.isArray(parsed)) {
                                              documentUrls = parsed;
                                            } else {
                                              // Single URL
                                              documentUrls = [transaction.document_url];
                                            }
                                          } catch (e) {
                                            // Not JSON, treat as single URL
                                            documentUrls = [transaction.document_url];
                                          }
                                        }
                                      }
                                      
                                      return documentUrls.map((url, index) => (
                                        <a
                                          key={index}
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-outline-primary btn-sm"
                                          title={`View Document ${index + 1}`}
                                        >
                                          <i className="fas fa-file-alt me-1"></i>
                                          Doc {index + 1}
                                        </a>
                                      ));
                                    })()}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </>
                          )}

                          <td className="text-end">
                            <div className="btn-group btn-group-sm" role="group">
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => {
                                  setViewTransaction(transaction);
                                  setShowViewModal(true);
                                  setError(null);
                                }}
                                title="View Transaction"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              {(() => {
                                const createdAt = new Date(transaction.updated_at);
                                const today = new Date();
                                const isSameDay =
                                  createdAt.getFullYear() === today.getFullYear() &&
                                  createdAt.getMonth() === today.getMonth() &&
                                  createdAt.getDate() === today.getDate();
                                const status = transaction.status;
                                const shouldShowButton =
                                  (status === 'pending' || status === 'partially_paid') ||
                                  ((status === 'paid') && isSameDay);
                                if (shouldShowButton) {
                                  return (
                                    <button
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => {
                                        handleEditTransaction(transaction);
                                        setError(null);
                                      }}
                                      title="Edit Transaction"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* Upload Document button - show for all transaction types that support documents */}
                              {((transaction.type === 'expense' || transaction.type === 'payment' || (transaction.category === 'booking' && transaction.unit_id)) && (
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => {
                                    handleUploadDocument(transaction);
                                    setError(null);
                                  }}
                                  title="Upload Documents"
                                >
                                  <i className="fas fa-upload"></i>
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="14" className="text-center py-4 text-muted">
                          <i className="fas fa-receipt fa-3x mb-3 d-block"></i>
                          <p>No transactions found matching your filters.</p>
                          <p>Try adjusting your filters or adding new transactions.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination Controls for Transactions */}
          {filteredTransactions.length > transactionsPerPage && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <div>
                Showing {filteredTransactions.length === 0 ? 0 : transactionsIndexOfFirst + 1} to {Math.min(transactionsIndexOfLast, filteredTransactions.length)} of {filteredTransactions.length} transactions
              </div>
              <nav aria-label="Transactions pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${transactionsPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => { setTransactionsPage(transactionsPage - 1); setTimeout(() => { scrollToSectionTop('transactions-section'); }, 50); }} disabled={transactionsPage === 1}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </li>
                  {Array.from({ length: transactionsTotalPages }).map((_, idx) => {
                    const pageNum = idx + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === transactionsTotalPages ||
                      (pageNum >= transactionsPage - 1 && pageNum <= transactionsPage + 1)
                    ) {
                      return (
                        <li key={pageNum} className={`page-item ${transactionsPage === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => { setTransactionsPage(pageNum); setTimeout(() => { scrollToSectionTop('transactions-section'); }, 50); }}>{pageNum}</button>
                        </li>
                      );
                    }
                    if (
                      (pageNum === 2 && transactionsPage > 3) ||
                      (pageNum === transactionsTotalPages - 1 && transactionsPage < transactionsTotalPages - 2)
                    ) {
                      return (
                        <li key={pageNum} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    return null;
                  })}
                  <li className={`page-item ${transactionsPage === transactionsTotalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => { setTransactionsPage(transactionsPage + 1); setTimeout(() => { scrollToSectionTop('transactions-section'); }, 50); }} disabled={transactionsPage === transactionsTotalPages}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Owner Payments Section (Admin Only) */}
      {!subscriptionStatus.isFreePlan && activeTab !== 'advanced' && (
        <div className="card shadow-sm my-4" id="owner-payments-section">
          <div className="card-header bg-light d-flex align-items-center">
            <h5 className="mb-0"><i className="fas fa-user-check me-2"></i>Submitted Owner Payments</h5>
            {ownerPaymentsLoading && <span className="ms-3 spinner-border spinner-border-sm text-primary" />}
          </div>
          <div className="card-body p-0">
            {ownerPaymentsError && (
              <div className="alert alert-danger m-3">{ownerPaymentsError}</div>
            )}
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Owner Name</th>
                    <th>Email</th>
                    <th>Unit</th>
                    <th>Category</th>
                    <th>Month</th>
                    <th>Screenshot</th>
                    <th>Submitted At</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerPayments.length === 0 && !ownerPaymentsLoading ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        <i className="fas fa-receipt fa-2x mb-2 d-block"></i>
                        No owner payments submitted yet.
                      </td>
                    </tr>
                  ) : (
                    paginatedOwnerPayments.map(payment => (
                      <tr key={payment.id}>
                        <td>#{payment.id}</td>
                        <td>{payment.owner_name || '-'}</td>
                        <td>{payment.owner_email || '-'}</td>
                        <td>
                          {payment.unit_id ? (
                            (() => {
                              const unit = units.find(u => u.id === payment.unit_id);
                              return unit ? `${unit.unit_type ? `${unit.unit_type} ` : ''}${unit.villa_number}` : `Unit #${payment.unit_id}`;
                            })()
                          ) : (
                            payment.villa_number || '-'
                          )}
                        </td>
                        <td className="text-capitalize">{payment.category ? payment.category.replace('_', ' ') : '-'}</td>
                        <td>{payment.month_id ? monthNames[payment.month_id] : '-'}</td>
                        <td>
                          {payment.screenshot_url ? (
                            <a className='badge rounded-pill bg-secondary text-decoration-none badge-hover-custom' href={payment.screenshot_url} target="_blank" rel="noopener noreferrer">View</a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{payment.created_at ? formatDateTime(payment.created_at) : '-'}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-outline-danger btn-sm"
                            title="Delete Owner Payment"
                            onClick={() => {
                              setOwnerPaymentToDelete(payment);
                              setShowDeleteOwnerPayment(true);
                            }}
                          >
                            <i className="fas fa-trash-alt"></i>
                            <span className="d-lg-inline ms-1">Delete</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            {ownerPayments.length > ownerPaymentsPerPage && (
              <div className="card-footer d-flex justify-content-between align-items-center">
                <div>
                  Showing {ownerPaymentsIndexOfFirst + 1} to {Math.min(ownerPaymentsIndexOfLast, ownerPayments.length)} of {ownerPayments.length} payments
                </div>
                <nav aria-label="Owner payments pagination">
                  <ul className="pagination mb-0">
                    <li className={`page-item ${ownerPaymentsPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => { setOwnerPaymentsPage(ownerPaymentsPage - 1); setTimeout(() => { scrollToSectionTop('owner-payments-section'); }, 50); }} disabled={ownerPaymentsPage === 1}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: ownerPaymentsTotalPages }).map((_, idx) => {
                      const pageNum = idx + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === ownerPaymentsTotalPages ||
                        (pageNum >= ownerPaymentsPage - 1 && pageNum <= ownerPaymentsPage + 1)
                      ) {
                        return (
                          <li key={pageNum} className={`page-item ${ownerPaymentsPage === pageNum ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => { setOwnerPaymentsPage(pageNum); setTimeout(() => { scrollToSectionTop('owner-payments-section'); }, 50); }}>{pageNum}</button>
                          </li>
                        );
                      }
                      if (
                        (pageNum === 2 && ownerPaymentsPage > 3) ||
                        (pageNum === ownerPaymentsTotalPages - 1 && ownerPaymentsPage < ownerPaymentsTotalPages - 2)
                      ) {
                        return (
                          <li key={pageNum} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li className={`page-item ${ownerPaymentsPage === ownerPaymentsTotalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => { setOwnerPaymentsPage(ownerPaymentsPage + 1); setTimeout(() => { scrollToSectionTop('owner-payments-section'); }, 50); }} disabled={ownerPaymentsPage === ownerPaymentsTotalPages}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advance Payments Section */}
      {activeTab === 'advanced' && !subscriptionStatus.isFreePlan && (
        <div className="card shadow-sm my-4">
          <div className="card-header bg-light d-flex align-items-center">
            <h5 className="mb-0"><i className="fas fa-hand-holding-usd me-2"></i>Advance Payments & Balances</h5>
            {advancedAmountsLoading && <span className="ms-3 spinner-border spinner-border-sm text-primary" />}
          </div>
          <div className="card-body p-0">
            {advancedAmountsError && (
              <div className="alert alert-danger m-3">{advancedAmountsError}</div>
            )}

            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Unit</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th className="text-end">Current Balance</th>
                    <th className="text-end">Last Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {advancedAmounts.length === 0 && !advancedAmountsLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        <i className="fas fa-hand-holding-usd fa-2x mb-2 d-block"></i>
                        No units with positive advance balance found.
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      // Ensure advancedAmounts is an array
                      const safeAdvancedAmounts = Array.isArray(advancedAmounts) ? advancedAmounts : [];

                      if (safeAdvancedAmounts.length === 0) {
                        return (
                          <tr>
                            <td colSpan="6" className="text-center text-muted py-4">
                              <i className="fas fa-hand-holding-usd fa-2x mb-2 d-block"></i>
                              No advance payments found.
                            </td>
                          </tr>
                        );
                      }

                      // Group advance amounts by unit_id and get the latest balance for each unit
                      const groupedByUnit = safeAdvancedAmounts.reduce((acc, amount) => {
                        const unitId = amount.unit_id;
                        if (!acc[unitId]) {
                          acc[unitId] = {
                            unit_id: unitId,
                            amounts: []
                          };
                        }
                        acc[unitId].amounts.push(amount);
                        return acc;
                      }, {});

                      // Get the latest balance for each unit
                      const unitBalances = Object.values(groupedByUnit).map(group => {
                        const sortedAmounts = group.amounts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                        const latestAmount = sortedAmounts[0];

                        return {
                          unit_id: group.unit_id,
                          unit: {
                            id: group.unit_id,
                            unit_type: latestAmount.unit_type,
                            villa_number: latestAmount.villa_number,
                            owner_name: latestAmount.owner_name,
                            owner_email: latestAmount.owner_email,
                            owner_phone: latestAmount.owner_phone
                          },
                          balance: latestAmount.balance || 0,
                          last_transaction_date: latestAmount.created_at,
                          last_transaction_type: latestAmount.advance_type,
                          last_transaction_amount: latestAmount.amount
                        };
                      });

                      // Filter to show only units with positive advance balance
                      const positiveBalanceUnits = unitBalances.filter(unitBalance => unitBalance.balance > 0);

                      if (positiveBalanceUnits.length === 0) {
                        return (
                          <tr>
                            <td colSpan="6" className="text-center text-muted py-4">
                              <i className="fas fa-hand-holding-usd fa-2x mb-2 d-block"></i>
                              No units with positive advance balance found.
                            </td>
                          </tr>
                        );
                      }

                      return positiveBalanceUnits.map(unitBalance => (
                        <tr key={unitBalance.unit_id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar-sm me-3">
                                <div className="avatar-title bg-primary text-white rounded-circle">
                                  {unitBalance.unit?.owner_name ? unitBalance.unit.owner_name.charAt(0).toUpperCase() : 'U'}
                                </div>
                              </div>
                              <div>
                                <h6 className="mb-0">{unitBalance.unit?.owner_name || 'Unknown Owner'}</h6>
                              </div>
                            </div>
                          </td>
                          <td>
                            {unitBalance.unit ? (
                              <span className="text-nowrap">
                                {unitBalance.unit.unit_type ? (
                                  <span className="badge bg-secondary me-1">{unitBalance.unit.unit_type}</span>
                                ) : null}
                                {unitBalance.unit.villa_number}
                              </span>
                            ) : (
                              <span className="text-muted">Unit #{unitBalance.unit_id}</span>
                            )}
                          </td>
                          <td>
                            <span className="text-break">{unitBalance.unit?.owner_email || '-'}</span>
                          </td>
                          <td>
                            <span className="text-nowrap">{unitBalance.unit?.owner_phone || '-'}</span>
                          </td>
                          <td className="text-end">
                            <span className={`fw-bold ${unitBalance.balance >= 0 ? 'text-success' : 'text-danger'}`}>
                              {safeFormatCurrency(unitBalance.balance)}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="text-nowrap">
                              <small className="text-muted d-block">
                                {unitBalance.last_transaction_date ?
                                  new Date(unitBalance.last_transaction_date).toLocaleDateString('en-IN') :
                                  'N/A'
                                }
                              </small>
                              {unitBalance.last_transaction_type && (
                                <small className={`badge ${unitBalance.last_transaction_type === 'credit' ? 'bg-success' : 'bg-danger'}`}>
                                  {unitBalance.last_transaction_type === 'credit' ? '+' : '-'}
                                  {safeFormatCurrency(unitBalance.last_transaction_amount)}
                                </small>
                              )}
                            </div>
                            <div className="btn-group btn-group-sm mt-1 " role="group">
                              <button
                                className="btn btn-outline-secondary btn-sm justify-content-center"
                                onClick={() => navigate(`/admin/units/${unitBalance.unit_id}`)}
                                title="View Unit Details"
                              >
                                <i className="fas fa-eye"></i>
                                <span className="d-lg-inline ms-1">View</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Owner Payment Modal */}
      {showDeleteOwnerPayment && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteOwnerPayment(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this owner payment?</p>
                {ownerPaymentToDelete && (
                  <div className="alert alert-warning">
                    <strong>Owner Payment Details:</strong><br />
                    ID: {ownerPaymentToDelete.id}<br />
                    Owner: {ownerPaymentToDelete.owner_name || '-'}<br />
                    Email: {ownerPaymentToDelete.owner_email || '-'}<br />
                    Unit: {ownerPaymentToDelete.unit_id ? (
                      (() => {
                        const unit = units.find(u => u.id === ownerPaymentToDelete.unit_id);
                        return unit ? `${unit.unit_type ? `${unit.unit_type} ` : ''}${unit.villa_number}` : `Unit #${ownerPaymentToDelete.unit_id}`;
                      })()
                    ) : (
                      ownerPaymentToDelete.villa_number || '-'
                    )}<br />
                    Category: {ownerPaymentToDelete.category ? ownerPaymentToDelete.category.replace('_', ' ') : '-'}<br />
                    Month: {ownerPaymentToDelete.month_id ? monthNames[ownerPaymentToDelete.month_id] : '-'}<br />
                  </div>
                )}
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteOwnerPayment(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteOwnerPayment}>
                  <i className="fas fa-trash-alt me-2"></i>
                  Delete Owner Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Transaction Modal */}

      {showViewModal && viewTransaction && (

        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">

          <div className="modal-dialog">

            <div className="modal-content">

              <div className="modal-header">

                <h5 className="modal-title" style={{

                  backgroundColor: bgColor,

                  color: textColor

                }}>Transaction Details</h5>

                <button type="button" className="btn-close"
                  onClick={() => setShowViewModal(false)}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>

              </div>

              <div className="modal-body">

                <ul className="list-group list-group-flush">

                  <li className="list-group-item"><strong>ID:</strong> {viewTransaction.id}</li>

                  <li className="list-group-item"><strong>Invoice Generated On:</strong> {formatDateTime(viewTransaction.created_at)}</li>

                  {(viewTransaction.partially_payment_date && viewTransaction.status === 'partially_paid' && !viewTransaction.use_advanced_amount) && (
                    <>
                      <li className="list-group-item"><strong>Partially Paid On:</strong> {viewTransaction.partially_payment_date}</li>
                      <li className="list-group-item"><strong>Partially Paid Amount:</strong> {safeFormatCurrency(viewTransaction.partially_paid_amount)}</li>
                      <li className="list-group-item"><strong>Remaining Amount:</strong> <span className="text-warning fw-bold ms-2">{safeFormatCurrency((viewTransaction.amount) - (viewTransaction.partially_paid_amount))}</span></li>
                    </>
                  )}

                  {(viewTransaction.partially_payment_date && viewTransaction.status === 'partially_paid' && viewTransaction.use_advanced_amount > 0) && (
                    <>
                      <li className="list-group-item"><strong>Partially Paid On:</strong> {viewTransaction.partially_payment_date}</li>
                      <li className="list-group-item"><strong>Partially Paid Amount:</strong> {safeFormatCurrency(viewTransaction.partially_paid_amount)}</li>
                      <li className="list-group-item"><strong>Remaining Amount:</strong> <span className="text-warning fw-bold ms-2">{safeFormatCurrency(viewTransaction.remaining_amount)}</span></li>
                    </>
                  )}

                  {(viewTransaction.partially_payment_date && viewTransaction.status === 'paid') && (
                    <>
                      <li className="list-group-item"><strong>Partially Paid On:</strong> {viewTransaction.partially_payment_date}</li>
                      <li className="list-group-item"><strong>Partially Paid Amount:</strong> <span className="text-warning fw-bold ms-2">{safeFormatCurrency(viewTransaction.partially_paid_amount)}</span></li>
                      <li className="list-group-item"><strong>Remaining Paid On:</strong> {viewTransaction.payment_date}</li>
                      <li className="list-group-item"><strong>Remaining Paid Amount:</strong> <span className="text-success fw-bold ms-2">{safeFormatCurrency((viewTransaction.amount) - (viewTransaction.partially_paid_amount))}</span></li>
                    </>
                  )}

                  {(!viewTransaction.partially_payment_date && viewTransaction.status === 'paid') && (
                    <>
                      <li className="list-group-item"><strong>Paid On:</strong> {viewTransaction.payment_date}</li>
                      <li className="list-group-item"><strong>Paid Amount:</strong> {safeFormatCurrency(viewTransaction.amount)}</li>
                    </>
                  )}

                  <li className="list-group-item"><strong>Type:</strong> {viewTransaction.type === 'payment' ? 'Income' : 'Expense'}</li>

                  <li className="list-group-item"><strong>Category:</strong> {viewTransaction.category ? viewTransaction.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A'}</li>

                  <li className="list-group-item"><strong>Total Amount:</strong> {safeFormatCurrency(viewTransaction.amount)}</li>

                  <li className="list-group-item">

                    <strong>Status:</strong>

                    <span className={`badge rounded-pill ms-2 ${viewTransaction.status === 'paid' ? 'bg-success' :

                      viewTransaction.status === 'pending' ? 'bg-warning text-dark' :

                        viewTransaction.status === 'hold' ? 'bg-secondary' :

                          viewTransaction.status === 'partially_paid' ? 'bg-info' : 'bg-light text-dark'

                      }`}>

                      {viewTransaction.status ? viewTransaction.status.replace('_', ' ').toUpperCase() : 'PAID'}

                    </span>

                  </li>

                  {/* Payment Method */}
                  <li className="list-group-item">
                    <strong>Payment Method:</strong>
                    <span className={`badge ms-2 ${viewTransaction.payment_method === 'cash_payment' ? 'bg-secondary' :
                      viewTransaction.payment_method === 'from_advance_balance' ? 'bg-primary' :
                        viewTransaction.payment_method === 'cash_from_advance_balance' ? 'bg-info' : 'bg-light text-dark'
                      }`}>
                      {viewTransaction.payment_method === 'cash_from_advance_balance' ?
                        'Cash + From Advance Balance' :
                        viewTransaction.payment_method ?
                          viewTransaction.payment_method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                          'Cash Payment'
                      }
                    </span>
                  </li>

                  {/* Payment Breakdown - Only show for payments with advance amount usage */}
                  {viewTransaction.type === 'payment' && viewTransaction.use_advanced_amount > 0 && (
                    <>
                      <li className="list-group-item">
                        <strong>Used Advance Amount:</strong>
                        <span className="badge bg-success ms-2">
                          <i className="fas fa-check me-1"></i>
                          Yes
                        </span>
                      </li>
                      <li className="list-group-item">
                        <strong>Advance Amount Used:</strong>
                        <span className="text-primary fw-bold ms-2">
                          {safeFormatCurrency(viewTransaction.advanced_amount_used || 0)}
                        </span>
                      </li>
                      <li className="list-group-item">
                        <strong>Cash Payment:</strong>
                        <span className="text-success fw-bold ms-2">
                          {safeFormatCurrency(viewTransaction.cash_payment || 0)}
                        </span>
                      </li>
                      {viewTransaction.payment_breakdown && (
                        <li className="list-group-item">
                          <strong>Payment Breakdown:</strong>
                          <div className="mt-2">
                            <div className="row text-center">
                              <div className="col-4">
                                <div className="border rounded p-2">
                                  <div className="text-muted small">Total</div>
                                  <div className="fw-bold">{safeFormatCurrency(viewTransaction.payment_breakdown.total || viewTransaction.amount)}</div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="border rounded p-2">
                                  <div className="text-muted small">Advance</div>
                                  <div className="fw-bold text-primary">{safeFormatCurrency(viewTransaction.payment_breakdown.advanced_used || 0)}</div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div className="border rounded p-2">
                                  <div className="text-muted small">Cash</div>
                                  <div className="fw-bold text-success">{safeFormatCurrency(viewTransaction.payment_breakdown.cash_amount || 0)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      )}
                    </>
                  )}

                  <li className="list-group-item"><strong>Unit:</strong> {viewTransaction.unit_id ? (() => {
                    const unit = units.find(u => u.id === viewTransaction.unit_id);
                    return unit ? `${unit.unit_type ? `${unit.unit_type} ` : ''}${unit.villa_number}${unit.owner_name ? ` (${unit.owner_name})` : ''}` : 'N/A';
                  })() : 'N/A'}</li>

                  <li className="list-group-item"><strong>Remarks:</strong> {viewTransaction.remarks || '-'}</li>

                  {/* Document Section - Only show for expense transactions with documents */}
                  {(() => {
                    // Handle both single URL and array formats dynamically
                    let documentUrls = [];
                    
                    if (viewTransaction.document_url) {
                      if (Array.isArray(viewTransaction.document_url)) {
                        // Already an array
                        documentUrls = viewTransaction.document_url;
                      } else if (typeof viewTransaction.document_url === 'string') {
                        // Check if it's a JSON string
                        try {
                          const parsed = JSON.parse(viewTransaction.document_url);
                          if (Array.isArray(parsed)) {
                            documentUrls = parsed;
                          } else {
                            // Single URL
                            documentUrls = [viewTransaction.document_url];
                          }
                        } catch (e) {
                          // Not JSON, treat as single URL
                          documentUrls = [viewTransaction.document_url];
                        }
                      }
                    }
                    
                    return (viewTransaction.type === 'expense' || viewTransaction.type === 'payment' || (viewTransaction.category === 'booking' && viewTransaction.unit_id)) && documentUrls.length > 0;
                  })() && (
                    <li className="list-group-item">
                      <strong>Documents:</strong>
                      <div className="mt-2">
                        <div className="d-flex flex-wrap gap-2">
                          {(() => {
                            // Handle both single URL and array formats dynamically
                            let documentUrls = [];
                            
                            if (viewTransaction.document_url) {
                              if (Array.isArray(viewTransaction.document_url)) {
                                // Already an array
                                documentUrls = viewTransaction.document_url;
                              } else if (typeof viewTransaction.document_url === 'string') {
                                // Check if it's a JSON string
                                try {
                                  const parsed = JSON.parse(viewTransaction.document_url);
                                  if (Array.isArray(parsed)) {
                                    documentUrls = parsed;
                                  } else {
                                    // Single URL
                                    documentUrls = [viewTransaction.document_url];
                                  }
                                } catch (e) {
                                  // Not JSON, treat as single URL
                                  documentUrls = [viewTransaction.document_url];
                                }
                              }
                            }
                            
                            return documentUrls.map((url, index) => (
                              <a
                                key={index}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm"
                                title={`View Document ${index + 1}`}
                              >
                                <i className="fas fa-file-alt me-2"></i>
                                Document {index + 1}
                              </a>
                            ));
                          })()}
                        </div>
                        <div className="text-muted small mt-1">
                          <i className="fas fa-info-circle me-1"></i>
                          Click to view receipt, invoice, or supporting document{(() => {
                            let documentUrls = [];
                            if (viewTransaction.document_url) {
                              if (Array.isArray(viewTransaction.document_url)) {
                                documentUrls = viewTransaction.document_url;
                              } else if (typeof viewTransaction.document_url === 'string') {
                                try {
                                  const parsed = JSON.parse(viewTransaction.document_url);
                                  documentUrls = Array.isArray(parsed) ? parsed : [viewTransaction.document_url];
                                } catch (e) {
                                  documentUrls = [viewTransaction.document_url];
                                }
                              }
                            }
                            return documentUrls.length > 1 ? 's' : '';
                          })()}
                        </div>
                      </div>
                    </li>
                  )}

                  {viewTransaction.month_id && (

                    <li className="list-group-item"><strong>Paid Month:</strong> {monthNames[viewTransaction.month_id]}</li>

                  )}

                </ul>

              </div>

              <div className="modal-footer">

                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* Document Upload Modal */}
      {showDocumentUploadModal && documentUploadTransaction && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className={`modal-header ${themeClassName}`}>
                <h5 className="modal-title">
                  <i className="fas fa-upload me-2"></i>
                  Upload Documents - {documentUploadTransaction.type === 'expense' ? 'Expense' : 'Income'} Transaction
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDocumentUploadModal(false);
                    setDocumentUploadTransaction(null);
                    setDocumentUploadFiles([]);
                    setDocumentUploadError(null);
                    setDocumentUploadSuccess(null);
                  }}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Transaction Details */}
                <div className="alert alert-info mb-3">
                  <h6 className="mb-2">Transaction Details:</h6>
                  <div className="row">
                    <div className="col-sm-6">
                      <strong>Type:</strong> {documentUploadTransaction.type === 'expense' ? 'Expense' : 'Income'}<br/>
                      <strong>Category:</strong> {documentUploadTransaction.category}<br/>
                      <strong>Amount:</strong> {safeFormatCurrency(documentUploadTransaction.amount)}
                    </div>
                    <div className="col-sm-6">
                      {documentUploadTransaction.unit_id && (() => {
                        const unit = units.find(u => u.id === documentUploadTransaction.unit_id);
                        return unit ? (
                          <>
                            <strong>Unit:</strong> {unit.villa_number}<br/>
                            {unit.owner_name && <><strong>Owner:</strong> {unit.owner_name}</>}
                          </>
                        ) : null;
                      })()}
                      <strong>Date:</strong> {new Date(documentUploadTransaction.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Existing Documents */}
                {(() => {
                  let existingUrls = [];
                  if (documentUploadTransaction.document_url) {
                    if (Array.isArray(documentUploadTransaction.document_url)) {
                      existingUrls = documentUploadTransaction.document_url;
                    } else if (typeof documentUploadTransaction.document_url === 'string') {
                      try {
                        const parsed = JSON.parse(documentUploadTransaction.document_url);
                        if (Array.isArray(parsed)) {
                          existingUrls = parsed;
                        } else {
                          existingUrls = [documentUploadTransaction.document_url];
                        }
                      } catch (e) {
                        existingUrls = [documentUploadTransaction.document_url];
                      }
                    }
                  }
                  
                  return existingUrls.length > 0;
                })() && (
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">Current Documents:</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {(() => {
                        let existingUrls = [];
                        if (documentUploadTransaction.document_url) {
                          if (Array.isArray(documentUploadTransaction.document_url)) {
                            existingUrls = documentUploadTransaction.document_url;
                          } else if (typeof documentUploadTransaction.document_url === 'string') {
                            try {
                              const parsed = JSON.parse(documentUploadTransaction.document_url);
                              if (Array.isArray(parsed)) {
                                existingUrls = parsed;
                              } else {
                                existingUrls = [documentUploadTransaction.document_url];
                              }
                            } catch (e) {
                              existingUrls = [documentUploadTransaction.document_url];
                            }
                          }
                        }
                        
                        return existingUrls.map((url, index) => (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                            title={`View Document ${index + 1}`}
                          >
                            <i className="fas fa-file-alt me-1"></i>
                            Doc {index + 1}
                          </a>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* File Upload Form */}
                <form onSubmit={handleDocumentUploadSubmit}>
                  <div className="mb-3">
                    <label htmlFor="documentUpload" className="form-label">
                      <i className="fas fa-file-upload me-2"></i>
                      Select Documents to Upload
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="documentUpload"
                      name="documentUpload"
                      onChange={handleDocumentFileChange}
                      accept=".jpg,.jpeg,.png,.pdf"
                      multiple
                    />
                    <div className="form-text">
                      <i className="fas fa-info-circle me-1"></i>
                      Upload receipts, invoices, or supporting documents. You can select multiple files. Accepted formats: JPG, JPEG, PNG, PDF (Max 10MB each)
                    </div>
                  </div>

                  {/* Selected Files */}
                  {documentUploadFiles.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">Selected Files:</h6>
                      {documentUploadFiles.map((file, index) => (
                        <div key={index} className="alert alert-info py-2 mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <i className="fas fa-check-circle me-2"></i>
                              <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveDocumentFile(index)}
                              title="Remove this file"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleRemoveDocumentFile()}
                        title="Remove all selected files"
                      >
                        <i className="fas fa-trash-alt me-1"></i>
                        Remove All Files
                      </button>
                    </div>
                  )}

                  {/* Error and Success Messages */}
                  {documentUploadError && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {documentUploadError}
                    </div>
                  )}

                  {documentUploadSuccess && (
                    <div className="alert alert-success">
                      <i className="fas fa-check-circle me-2"></i>
                      {documentUploadSuccess}
                    </div>
                  )}
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDocumentUploadModal(false);
                    setDocumentUploadTransaction(null);
                    setDocumentUploadFiles([]);
                    setDocumentUploadError(null);
                    setDocumentUploadSuccess(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleDocumentUploadSubmit}
                  disabled={documentUploadFiles.length === 0}
                >
                  <i className="fas fa-upload me-2"></i>
                  Upload Documents
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`

        .badge-hover-custom:hover {
          color: #ffffff !important;
        }
        /* Custom filter styles */
        .filters-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: 1px solid #dee2e6;
        }
        .filter-label {
          font-weight: 600;
          color: #495057;
          font-size: 0.875rem;
        }
        .results-count {
          font-size: 0.875rem;
          color: #6c757d;
          font-weight: 500;
          background: #fff;
          padding: 0.5rem;
          border-radius: 0.25rem;
          border: 1px solid #dee2e6;
        }
        .clear-filters-btn {
          transition: all 0.2s ease;
        }
        .clear-filters-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .filters-card .row > div {
            margin-bottom: 0.75rem;
          }
          .clear-filters-btn {
            width: 100%;
          }
          .results-count {
            text-align: center;
            margin-top: 0.5rem;
          }
        }
        /* Form control enhancements */
        .form-select:focus,
        .form-control:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        /* Clickable card styles */
        .clickable-card {
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }
        .clickable-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.15);
        }
        .clickable-card:active {
          transform: translateY(0);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .click-hint {
          font-size: 0.75rem;
          color: #6c757d;
          margin-top: 0.25rem;
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }
        .clickable-card:hover .click-hint {
          opacity: 1;
          color: #ffc107;
        }
        
        /* advance amount Button Styles */
        .btn-success {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          border: none;
          transition: all 0.3s ease;
        }
        
        .btn-success:hover {
          background: linear-gradient(135deg, #218838 0%, #1e7e34 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
        }
        
        /* Advance Form Specific Styles */
        #advancedForm .alert-info {
          border-left: 4px solid #17a2b8;
          background: linear-gradient(135deg, #d1ecf1 0%, #b8daff 100%);
        }
        
        #advancedForm .alert-warning {
          border-left: 4px solid #ffc107;
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
        }
        
        /* Enhanced form styling */
        .form-label .text-danger {
          font-weight: bold;
        }
        
        .modal-header.${themeClassName} {
          background: linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%);
        }
        
        /* Avatar styles */
        .avatar-sm {
          width: 40px;
          height: 40px;
        }
        
        .avatar-title {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 16px;
        }
      `}</style>
    </div>

  );
};

export default Financials;